import {
	EVENT_BUILDING_UNLOCK_LEVEL,
	IS_DEV,
	OFFER_BUILDING_UNLOCK_LEVEL,
	premiumBuildingsPrices,
} from "@/config/constants";
import { isActiveEventProductionForEntity } from "@/event";
import { ResourceType } from "@/game/entities/config/resources";
import { getUpgradeCurrentReplaceValueById } from "@/game/utils/upgrades";
import useSettlementsStore from "@/store/useSettlementsStore";
import useUpgradesStore from "@/store/useUpgradesStore";
import {
	BuffType,
	TileAction,
	type ProductionValue,
	type TileConfig,
	type TileConfigItem,
	type TileIndex,
	type UpgradeParams,
} from "@/types/tile";
import { ceilNumber } from "@/utils/number";
import {
	FrameName,
	tilerepoSettlementCastleBuildingsFrameNames,
	tilerepoSettlementEmptyFrameNames,
} from ".";
import type SettlementCastleBuildingTileEntity from "../buildings/SettlementCastleBuildingTileEntity";
import type EventTileEntity from "../EventTileEntity";
import type TileEntity from "../TileEntity";
import { EventUpgradeIds, UpgradeIds } from "./upgrades";

/** CONFIG **/

const getAccumulatedValue = (
	entities: Array<TileEntity>,
	resourceType:
		| typeof ResourceType.PRODUCTION_RESOURCE_MULTIPLIER
		| typeof ResourceType.PRODUCTION_RESOURCE_UNIT
		| typeof ResourceType.STORAGE_RESOURCE_MULTIPLIER,
	limit = Infinity,
): number => {
	let processed = 0;
	return entities.reduce(
		(acc: number, { production }: TileEntity): number => {
			if (processed >= limit) return acc;
			const value = production.has(resourceType)
				? (production.get(resourceType) as number)
				: 0;
			if (!value) return acc;
			processed++;
			return (
				acc +
				value -
				// fix pre multiplicator, aby 1.25 pripocitalo len ako 0.25, jednotka sa tam pricitava extra ako base multiplicator
				([
					ResourceType.PRODUCTION_RESOURCE_MULTIPLIER,
					ResourceType.STORAGE_RESOURCE_MULTIPLIER,
				].includes(resourceType) && value >= 1
					? 1
					: 0)
			);
		},
		0,
	);
};

// TODO: second param as object
export const getProductionResourceUnit = (
	entities: Array<TileEntity>,
	limit = Infinity,
): number =>
	getAccumulatedValue(entities, ResourceType.PRODUCTION_RESOURCE_UNIT, limit);

// TODO: second param as object
export const getProductionResourceMultiplier = (
	entities: Array<TileEntity>,
	limit = Infinity,
	unique = false,
): number =>
	getAccumulatedValue(
		entities.filter(
			(entity: TileEntity, index: number, array: Array<TileEntity>) =>
				!unique ||
				array.findIndex(
					({ frameName }: TileEntity): boolean =>
						frameName === entity.frameName,
				) === index,
		),
		ResourceType.PRODUCTION_RESOURCE_MULTIPLIER,
		limit,
	);

// TODO: second param as object
export const getStorageResourceMultiplier = (
	entities: Array<TileEntity>,
	limit = Infinity,
	unique = false,
): number =>
	getAccumulatedValue(
		entities.filter(
			(entity: TileEntity, index: number, array: Array<TileEntity>) =>
				!unique ||
				array.findIndex(
					({ frameName }: TileEntity): boolean =>
						frameName === entity.frameName,
				) === index,
		),
		ResourceType.STORAGE_RESOURCE_MULTIPLIER,
		limit,
	);

const config: TileConfig = {
	[FrameName.SETTLEMENT_CASTLE]: {
		exploreCoefficient: 1,
		requirements: new Map<ResourceType, number>(),
		productionBase: {},
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number => {
					const base = 10;
					const multiplier =
						1 +
						getProductionResourceMultiplier(
							entity.getOwnNeighbourTileEntities(),
						) +
						getUpgradeCurrentReplaceValueById(
							UpgradeIds.tax_season,
						);
					return ceilNumber(base * multiplier);
				},
			],
			[
				ResourceType.FOOD_INCOME,
				(entity: TileEntity): number => {
					if (
						!(entity as SettlementCastleBuildingTileEntity)
							.isMainSettlement
					)
						return 0;
					return IS_DEV ? 15_000 : 60;
				},
			],
			// Zbytok je v settlements.ts
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>(),
		range: 1,
		// Toto sa radsej riesi priamo v tej jednej budove, lebo hrad negeneruje ani unit ani multiplier.
		// supplies: new Set<FrameName>([FrameName.ENKINDLED_HOUSEHOLD]),
	},
	[FrameName.WHEAT_FIELD]: {
		requirements: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		production: new Map<ResourceType, number>([
			[ResourceType.PRODUCTION_RESOURCE_UNIT, 1],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					price: {
						[ResourceType.COINS]: 1_000,
					},
					text: "button.remove",
				},
			],
			[
				FrameName.DIRT_ROAD,
				{
					price: {
						[ResourceType.COINS]: 1_300,
					},
				},
			],
			[
				FrameName.GRAVEL_ROAD,
				{
					price: {
						[ResourceType.COINS]: 2_000,
						[ResourceType.STONE]: 50,
					},
				},
			],
			[
				FrameName.COBBLED_ROAD,
				{
					price: {
						[ResourceType.COINS]: 6_000,
						[ResourceType.STONE]: 500,
					},
				},
			],
		]),
		action: TileAction.REMOVE,
		supplies: new Set<FrameName>([
			FrameName.WHEAT_FARM,
			FrameName.SOLSTICE_WINDMILL,
		]),
	},
	[FrameName.HOPS_FIELD]: {
		requirements: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		production: new Map<ResourceType, number>([
			[ResourceType.PRODUCTION_RESOURCE_UNIT, 1],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					price: {
						[ResourceType.COINS]: 1_000,
					},
					text: "button.remove",
				},
			],
			[
				FrameName.DIRT_ROAD,
				{
					price: {
						[ResourceType.COINS]: 1_300,
					},
				},
			],
			[
				FrameName.GRAVEL_ROAD,
				{
					price: {
						[ResourceType.COINS]: 2_000,
						[ResourceType.STONE]: 50,
					},
				},
			],
			[
				FrameName.COBBLED_ROAD,
				{
					price: {
						[ResourceType.COINS]: 6_000,
						[ResourceType.STONE]: 500,
					},
				},
			],
		]),
		action: TileAction.REMOVE,
		supplies: new Set<FrameName>([
			FrameName.BREWERY,
			FrameName.LARGE_BREWERY,
		]),
	},
	[FrameName.CATTLE_RANGE]: {
		requirements: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		production: new Map<ResourceType, number>([
			[ResourceType.PRODUCTION_RESOURCE_UNIT, 1],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					price: {
						[ResourceType.COINS]: 2_500,
					},
					text: "button.remove",
				},
			],
			[
				FrameName.DIRT_ROAD,
				{
					price: {
						[ResourceType.COINS]: 12_800,
					},
				},
			],
			[
				FrameName.GRAVEL_ROAD,
				{
					price: {
						[ResourceType.COINS]: 3_500,
						[ResourceType.STONE]: 50,
					},
				},
			],
			[
				FrameName.COBBLED_ROAD,
				{
					price: {
						[ResourceType.COINS]: 7_500,
						[ResourceType.STONE]: 500,
					},
				},
			],
		]),
		action: TileAction.REMOVE,
		supplies: new Set<FrameName>([FrameName.LIVESTOCK_FARM]),
	},
	[FrameName.PUMPKIN_FIELD]: {
		requirements: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		production: new Map<ResourceType, number>([
			[ResourceType.PRODUCTION_RESOURCE_UNIT, 1],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					price: {
						[ResourceType.COINS]: 1_000,
					},
					text: "button.remove",
				},
			],
			[
				FrameName.DIRT_ROAD,
				{
					price: {
						[ResourceType.COINS]: 1_300,
					},
				},
			],
			[
				FrameName.GRAVEL_ROAD,
				{
					price: {
						[ResourceType.COINS]: 2_000,
						[ResourceType.STONE]: 50,
					},
				},
			],
			[
				FrameName.COBBLED_ROAD,
				{
					price: {
						[ResourceType.COINS]: 6_000,
						[ResourceType.STONE]: 500,
					},
				},
			],
		]),
		action: TileAction.REMOVE,
		supplies: new Set<FrameName>([FrameName.PUMPKIN_FARM]),
	},
	[FrameName.SEAWEED_FIELD]: {
		requirements: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		production: new Map<ResourceType, number>([
			[ResourceType.PRODUCTION_RESOURCE_UNIT, 1],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					price: {
						[ResourceType.COINS]: 1_000,
					},
					text: "button.remove",
				},
			],
		]),
		action: TileAction.REMOVE,
		supplies: new Set<FrameName>([FrameName.SEAWEED_FARM]),
	},
	[FrameName.APPLE_GROVE]: {
		requirements: new Map<ResourceType, number>([
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		production: new Map<ResourceType, number>([
			[ResourceType.PRODUCTION_RESOURCE_UNIT, 1],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					price: {
						[ResourceType.COINS]: 1_000,
					},
					text: "button.remove",
				},
			],
			[
				FrameName.DIRT_ROAD,
				{
					price: {
						[ResourceType.COINS]: 1_300,
					},
				},
			],
			[
				FrameName.GRAVEL_ROAD,
				{
					price: {
						[ResourceType.COINS]: 2_000,
						[ResourceType.STONE]: 50,
					},
				},
			],
			[
				FrameName.COBBLED_ROAD,
				{
					price: {
						[ResourceType.COINS]: 6_000,
						[ResourceType.STONE]: 500,
					},
				},
			],
		]),
		action: TileAction.REMOVE,
		supplies: new Set<FrameName>([FrameName.APPLE_ORCHARD]),
	},
	[FrameName.GATHERERS_HUT]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 1],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {},
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.FOOD_INCOME,
				(): number => {
					const base = 20;
					const multiplier =
						1 +
						getUpgradeCurrentReplaceValueById(
							UpgradeIds.fruit_baskets,
						);
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 10],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(600 / 4),
					},
				},
			],
		]),
		recommendedUpgrades: [2],
	},
	[FrameName.LUMBER_MILL]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 4],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {
			[ResourceType.LUMBER_INCOME]: {
				value: 1,
				frameName: FrameName.FOREST,
			},
		},
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.LUMBER_INCOME,
				(entity: TileEntity): number => {
					const base = getProductionResourceUnit(
						entity.getOwnNeighbourTerrainTileEntities("range"),
					);
					const multiplier =
						1 +
						getUpgradeCurrentReplaceValueById(UpgradeIds.bow_saw);
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 10],
		]),
		storage: new Map<ResourceType, number>([[ResourceType.LUMBER, 1_000]]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(1_500 / 4),
					},
				},
			],
			[
				FrameName.INDUSTRIAL_LUMBER_MILL,
				{
					price: {
						[ResourceType.GEMS]:
							premiumBuildingsPrices[
								FrameName.INDUSTRIAL_LUMBER_MILL
							],
					},
					reward: {
						[ResourceType.COINS]: ceilNumber(1_500 / 4),
					},
				},
			],
		]),
		range: 2,
		recommendedUpgrades: [5, 7],
	},
	[FrameName.SMALL_HOUSES]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 3],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {},
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number => {
					const base =
						20 +
						getUpgradeCurrentReplaceValueById(UpgradeIds.attic) +
						getUpgradeCurrentReplaceValueById(
							UpgradeIds.sanitation_system,
						);
					const multiplier =
						1 +
						getProductionResourceMultiplier(
							entity.getOwnNeighbourTileEntities(),
						);
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 10],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(2_000 / 4),
					},
				},
			],
			[
				FrameName.MEDIUM_HOUSES,
				{
					price: {
						[ResourceType.COINS]: 33_000,
						[ResourceType.LUMBER]: 10_000,
						[ResourceType.STONE]: 5_000,
						[ResourceType.CLAY]: 1_500,
					},
				},
			],
			[
				FrameName.LARGE_HOUSES,
				{
					price: {
						[ResourceType.COINS]: 498_000,
						[ResourceType.LUMBER]: 150_000,
						[ResourceType.STONE]: 100_000,
						[ResourceType.CLAY]: 50_000,
						[ResourceType.IRON]: 50_000,
						[ResourceType.STEEL]: 20_000,
					},
				},
			],
			[
				FrameName.CARPENTRY_DISTRICT,
				{
					price: {
						[ResourceType.GEMS]:
							premiumBuildingsPrices[
								FrameName.CARPENTRY_DISTRICT
							],
					},
					reward: {
						[ResourceType.COINS]: ceilNumber(2_000 / 4),
					},
				},
			],
			[
				FrameName.STONEMASONRY_DISTRICT,
				{
					price: {
						[ResourceType.GEMS]:
							premiumBuildingsPrices[
								FrameName.STONEMASONRY_DISTRICT
							],
					},
					reward: {
						[ResourceType.COINS]: ceilNumber(2_000 / 4),
					},
				},
			],
		]),
		range: 2, // cely settlement
	},
	[FrameName.WHEAT_FARM]: {
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 7],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {
			[ResourceType.FOOD_INCOME]: {
				value: 3,
				frameName: FrameName.WHEAT_FIELD,
			},
		},
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.FOOD_INCOME,
				(entity: TileEntity): number => {
					const base =
						3 *
						getProductionResourceUnit(
							entity.getOwnNeighbourTileEntities(),
						);
					const mills =
						entity.getOwnNeighbourTileEntitiesByNeighbourFrameName(
							FrameName.MILL,
							1,
						);
					const waterMills =
						entity.getOwnNeighbourTileEntitiesByNeighbourFrameName(
							FrameName.WATER_MILL,
							1,
						);
					const multiplier =
						1 +
						getProductionResourceMultiplier(mills) +
						getProductionResourceMultiplier(waterMills) +
						getUpgradeCurrentReplaceValueById(
							UpgradeIds.horse_collar,
						);
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 20],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(5_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(1_000 / 4),
					},
				},
			],
		]),
		range: 2,
		recommendedUpgrades: [8, 10],
	},
	[FrameName.GOLD_MINE]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 2],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {},
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(entity: TileEntity): number => {
					const base = 100;
					const goldProspectors =
						entity.getOwnNeighbourTileEntitiesByNeighbourFrameName(
							FrameName.GOLD_PROSPECTOR,
							1,
						);
					const multiplier =
						1 +
						getProductionResourceMultiplier(goldProspectors) +
						getUpgradeCurrentReplaceValueById(
							UpgradeIds.shaft_mining,
						);
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 30],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(5_000 / 4),
					},
				},
			],
			[
				FrameName.PREMIUM_GOLD_MINE,
				{
					price: {
						[ResourceType.GEMS]:
							premiumBuildingsPrices[FrameName.PREMIUM_GOLD_MINE],
					},
					reward: {
						[ResourceType.COINS]: ceilNumber(2_500 / 4),
					},
				},
			],
		]),
		recommendedUpgrades: [49, 50],
	},
	[FrameName.PREMIUM_GOLD_MINE]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 5],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {},
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(entity: TileEntity): number => {
					const base = 300;
					const goldProspectors =
						entity.getOwnNeighbourTileEntitiesByNeighbourFrameName(
							FrameName.GOLD_PROSPECTOR,
							1,
						);
					const multiplier =
						1 +
						getProductionResourceMultiplier(goldProspectors) +
						getUpgradeCurrentReplaceValueById(
							UpgradeIds.shaft_mining,
						) +
						getUpgradeCurrentReplaceValueById(UpgradeIds.gold_rush);
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 20],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
		recommendedUpgrades: [50],
	},
	[FrameName.STONE_QUARRY]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 10],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {},
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.STONE_INCOME,
				(entity: TileEntity): number => {
					const base = 10;
					const oxenStables =
						entity.getOwnNeighbourTileEntitiesByNeighbourFrameName(
							FrameName.OXEN_STABLE,
							1,
						);
					const multiplier =
						1 +
						getProductionResourceMultiplier(oxenStables) +
						getUpgradeCurrentReplaceValueById(UpgradeIds.pickaxe);
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 30],
		]),
		storage: new Map<ResourceType, number>([[ResourceType.STONE, 500]]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(10_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(2_500 / 4),
					},
				},
			],
			[
				FrameName.LARGE_STONE_QUARRY,
				{
					price: {
						[ResourceType.GEMS]:
							premiumBuildingsPrices[
								FrameName.LARGE_STONE_QUARRY
							],
					},
					reward: {
						[ResourceType.COINS]: ceilNumber(7_500 / 4),
						[ResourceType.LUMBER]: ceilNumber(2_500 / 4),
					},
				},
			],
		]),
		recommendedUpgrades: [13],
	},
	[FrameName.BAKERY]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 12],
			// [ResourceType.EARLY_ACCESS_LEVEL, 9],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {},
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.FOOD_INCOME,
				(): number => {
					const base = 80;
					const multiplier =
						1 +
						getUpgradeCurrentReplaceValueById(
							UpgradeIds.smooth_flour,
						);
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 25],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(15_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(2_500 / 4),
						[ResourceType.STONE]: ceilNumber(1_000 / 4),
					},
				},
			],
		]),
		recommendedUpgrades: [57],
	},
	[FrameName.INDUSTRIAL_LUMBER_MILL]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 9],
			// [ResourceType.EARLY_ACCESS_LEVEL, 7],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {
			[ResourceType.LUMBER_INCOME]: {
				value: 3,
				frameName: FrameName.FOREST,
			},
		},
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.LUMBER_INCOME,
				(entity: TileEntity): number => {
					const base =
						3 *
						getProductionResourceUnit(
							entity.getOwnNeighbourTerrainTileEntities("range"),
						);
					const multiplier =
						1 +
						getUpgradeCurrentReplaceValueById(UpgradeIds.bow_saw) +
						getUpgradeCurrentReplaceValueById(
							UpgradeIds.two_man_saw,
						);
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 5],
		]),
		storage: new Map<ResourceType, number>([[ResourceType.LUMBER, 5_000]]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
		range: 2,
		recommendedUpgrades: [58],
	},
	[FrameName.SHIPYARD]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 13],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {},
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 30],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(20_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(10_000 / 4),
						[ResourceType.STONE]: ceilNumber(2_500 / 4),
					},
				},
			],
		]),
		recommendedUpgrades: [20, 53, 56],
	},
	[FrameName.SHALLOW_SEA_FISHING_BOAT]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 13],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {
			[ResourceType.FOOD_INCOME]: {
				value: 2,
				frameName: FrameName.SHALLOW_SEA_SMALL_FISH,
			},
		},
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.FOOD_INCOME,
				(entity: TileEntity): number => {
					const base = getProductionResourceUnit(
						entity.getOwnNeighbourTerrainTileEntities("range"),
					);
					const multiplier = 1;
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 15],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
		range: 2,
		recommendedUpgrades: [20, 43],
	},
	[FrameName.DEEP_SEA_FISHING_BOAT]: {
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 13],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {
			[ResourceType.FOOD_INCOME]: {
				value: 2,
				frameName: FrameName.SHALLOW_SEA_SMALL_FISH,
			},
		},
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.FOOD_INCOME,
				(entity: TileEntity): number => {
					const base = getProductionResourceUnit(
						entity.getOwnNeighbourTerrainTileEntities("range"),
					);
					const multiplier = 1;
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 15],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
		range: 2,
		recommendedUpgrades: [20, 43],
	},
	[FrameName.TOLL_HOUSE]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 14],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {},
		production: new Map<ResourceType, number>([
			[ResourceType.PRODUCTION_ONCLICK_RESOURCE_MULTIPLIER, 1.2],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 50],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(50_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(15_000 / 4),
						[ResourceType.STONE]: ceilNumber(7_500 / 4),
					},
				},
			],
			[
				FrameName.TAX_OFFICE,
				{
					price: {
						[ResourceType.GEMS]:
							premiumBuildingsPrices[FrameName.TAX_OFFICE],
					},
					reward: {
						[ResourceType.COINS]: ceilNumber(50_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(15_000 / 4),
						[ResourceType.STONE]: ceilNumber(7_500 / 4),
					},
				},
			],
		]),
		supplies: new Set<FrameName>([
			...tilerepoSettlementCastleBuildingsFrameNames,
		]),
	},
	[FrameName.CLAY_PIT]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 15],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {},
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.CLAY_INCOME,
				(entity: TileEntity): number => {
					const base = 5;
					const brickyards =
						entity.getOwnNeighbourTileEntitiesByNeighbourFrameName(
							FrameName.BRICKYARD,
							1,
						);
					const multiplier =
						1 +
						getProductionResourceMultiplier(brickyards) +
						getUpgradeCurrentReplaceValueById(
							UpgradeIds.beasts_of_burden,
						) +
						getUpgradeCurrentReplaceValueById(
							UpgradeIds.beasts_of_burden2,
						);
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 25],
		]),
		storage: new Map<ResourceType, number>([[ResourceType.CLAY, 250]]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(25_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(7_500 / 4),
						[ResourceType.STONE]: ceilNumber(2_000 / 4),
					},
				},
			],
			[
				FrameName.LARGE_CLAY_PIT,
				{
					price: {
						[ResourceType.GEMS]:
							premiumBuildingsPrices[FrameName.LARGE_CLAY_PIT],
					},
					reward: {
						[ResourceType.COINS]: ceilNumber(20_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(7_500 / 4),
						[ResourceType.STONE]: ceilNumber(2_000 / 4),
					},
				},
			],
		]),
		recommendedUpgrades: [16],
	},
	[FrameName.SMALL_STOCKPILE]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 16],
			// [ResourceType.EARLY_ACCESS_LEVEL, 14],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {},
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 10],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.LUMBER, 2_500],
			[ResourceType.STONE, 1_250],
			[ResourceType.CLAY, 750],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(50_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(7_500 / 4),
						[ResourceType.STONE]: ceilNumber(5_000 / 4),
						[ResourceType.CLAY]: ceilNumber(1_500 / 4),
					},
				},
			],
			[
				FrameName.UNIVERSAL_WAREHOUSE,
				{
					price: {
						[ResourceType.GEMS]:
							premiumBuildingsPrices[
								FrameName.UNIVERSAL_WAREHOUSE
							],
					},
					reward: {
						[ResourceType.COINS]: ceilNumber(50_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(7_500 / 4),
						[ResourceType.STONE]: ceilNumber(5_000 / 4),
						[ResourceType.CLAY]: ceilNumber(1_500 / 4),
					},
				},
			],
		]),
		recommendedUpgrades: [23],
	},
	[FrameName.IRON_MINE]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 20],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {},
		production: new Map<ResourceType, number>([
			[ResourceType.PRODUCTION_RESOURCE_UNIT, 1],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 40],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(25_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(5_000 / 4),
						[ResourceType.STONE]: ceilNumber(2_500 / 4),
						[ResourceType.CLAY]: ceilNumber(500 / 4),
					},
				},
			],
			[
				FrameName.ADVANCED_IRON_MINE,
				{
					price: {
						[ResourceType.GEMS]: 100,
					},
					reward: {
						[ResourceType.COINS]: ceilNumber(25_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(5_000 / 4),
						[ResourceType.STONE]: ceilNumber(2_500 / 4),
						[ResourceType.CLAY]: ceilNumber(500 / 4),
					},
				},
			],
		]),
		supplies: new Set<FrameName>([
			FrameName.FURNACE,
			FrameName.HILLS_BLAST_FURNACE,
		]),
	},
	[FrameName.FURNACE]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 20],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {
			[ResourceType.IRON_INCOME]: {
				value: 4,
				frameName: FrameName.IRON_MINE,
			},
		},
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.IRON_INCOME,
				(entity: TileEntity): number => {
					const base =
						4 *
						getProductionResourceUnit(
							entity.getOwnNeighbourTileEntities(),
						);
					const multiplier =
						1 +
						getUpgradeCurrentReplaceValueById(UpgradeIds.bellows);
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 25],
		]),
		storage: new Map<ResourceType, number>([[ResourceType.IRON, 750]]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(150_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(25_000 / 4),
						[ResourceType.STONE]: ceilNumber(10_000 / 4),
						[ResourceType.CLAY]: ceilNumber(2_500 / 4),
					},
				},
			],
		]),
		range: 2,
		supplies: new Set<FrameName>([
			FrameName.FOUNDRY,
			FrameName.INDUSTRIAL_FOUNDRY,
		]),
		recommendedUpgrades: [19],
	},
	[FrameName.MILL]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 26],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {},
		production: new Map<ResourceType, number>([
			[ResourceType.PRODUCTION_RESOURCE_MULTIPLIER, 1.5],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 40],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(100_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(25_000 / 4),
						[ResourceType.STONE]: ceilNumber(25_000 / 4),
						[ResourceType.CLAY]: ceilNumber(10_000 / 4),
						[ResourceType.IRON]: ceilNumber(5_000 / 4),
						[ResourceType.GOODS]: ceilNumber(500 / 4),
					},
				},
			],
		]),
		range: 3,
		supplies: new Set<FrameName>([FrameName.WHEAT_FARM]),
		recommendedUpgrades: [61],
	},
	[FrameName.OXEN_STABLE]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 27],
			// [ResourceType.EARLY_ACCESS_LEVEL, 14],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {},
		production: new Map<ResourceType, number>([
			[ResourceType.PRODUCTION_RESOURCE_MULTIPLIER, 1.5],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 5],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
		range: 2,
		supplies: new Set<FrameName>([
			FrameName.STONE_QUARRY,
			FrameName.LARGE_STONE_QUARRY,
		]),
	},
	[FrameName.BLACKSMITH]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 25],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {},
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.GOODS_INCOME,
				(): number => {
					const base = 10;
					const multiplier =
						1 +
						getUpgradeCurrentReplaceValueById(
							UpgradeIds.hammer_and_tongs,
						);
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 40],
			[ResourceType.IRON_CONSUMPTION, 20],
		]),
		storage: new Map<ResourceType, number>([[ResourceType.GOODS, 300]]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(150_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(50_000 / 4),
						[ResourceType.STONE]: ceilNumber(20_000 / 4),
						[ResourceType.CLAY]: ceilNumber(10_000 / 4),
						[ResourceType.IRON]: ceilNumber(7_500 / 4),
					},
				},
			],
		]),
		recommendedUpgrades: [24],
	},
	[FrameName.MARKET]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 28],
			// [ResourceType.EARLY_ACCESS_LEVEL, 26],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {},
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(): number => {
					const base = 100;
					const multiplier =
						1 +
						getUpgradeCurrentReplaceValueById(
							UpgradeIds.exotic_goods,
						);
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 75],
			[ResourceType.GOODS_CONSUMPTION, 10],
		]),
		storage: new Map<ResourceType, number>([[ResourceType.GOODS, 200]]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(250_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(75_000 / 4),
						[ResourceType.STONE]: ceilNumber(50_000 / 4),
						[ResourceType.CLAY]: ceilNumber(20_000 / 4),
						[ResourceType.IRON]: ceilNumber(10_000 / 4),
						[ResourceType.GOODS]: ceilNumber(1_000 / 4),
					},
				},
			],
		]),
	},
	[FrameName.SHALLOW_SEA_SMALL_FISH_TRAP]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 22],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {
			[ResourceType.FOOD_INCOME]: {
				value: 10,
				frameName: FrameName.SHALLOW_SEA_SMALL_FISH,
			},
		},
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.FOOD_INCOME,
				(entity: TileEntity): number => {
					const base =
						5 *
						getProductionResourceUnit(
							entity.getOwnNeighbourTerrainTileEntities("self"),
						);
					const multiplier = 1;
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 10],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(15_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(5_000 / 4),
					},
				},
			],
		]),
	},
	[FrameName.SHALLOW_SEA_MEDIUM_FISH_TRAP]: {
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 22],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {
			[ResourceType.FOOD_INCOME]: {
				value: 30,
				frameName: FrameName.SHALLOW_SEA_MEDIUM_FISH,
			},
		},
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.FOOD_INCOME,
				(entity: TileEntity): number => {
					const base =
						5 *
						getProductionResourceUnit(
							entity.getOwnNeighbourTerrainTileEntities("self"),
						);
					const multiplier = 1;
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 10],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(15_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(5_000 / 4),
					},
				},
			],
		]),
	},
	[FrameName.FOUNDRY]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 31],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {
			[ResourceType.STEEL_INCOME]: {
				value: 10,
			},
		},
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.STEEL_INCOME,
				(entity: TileEntity): number => {
					const base =
						entity.getOwnNeighbourTileEntities().length > 0
							? 10
							: 0;
					const multiplier =
						1 +
						getUpgradeCurrentReplaceValueById(UpgradeIds.smelting);
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 75],
			[ResourceType.IRON_CONSUMPTION, 20],
		]),
		storage: new Map<ResourceType, number>([[ResourceType.STEEL, 300]]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(100_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(25_000 / 4),
						[ResourceType.STONE]: ceilNumber(20_000 / 4),
						[ResourceType.CLAY]: ceilNumber(10_000 / 4),
						[ResourceType.IRON]: ceilNumber(5_000 / 4),
						[ResourceType.GOODS]: ceilNumber(2_000 / 4),
					},
				},
			],
			[
				FrameName.INDUSTRIAL_FOUNDRY,
				{
					price: {
						[ResourceType.GEMS]:
							premiumBuildingsPrices[
								FrameName.INDUSTRIAL_FOUNDRY
							],
					},
					reward: {
						[ResourceType.COINS]: ceilNumber(100_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(25_000 / 4),
						[ResourceType.STONE]: ceilNumber(20_000 / 4),
						[ResourceType.CLAY]: ceilNumber(10_000 / 4),
						[ResourceType.IRON]: ceilNumber(5_000 / 4),
						[ResourceType.GOODS]: ceilNumber(2_000 / 4),
					},
				},
			],
		]),
		range: 2,
		recommendedUpgrades: [26],
	},
	[FrameName.WAREHOUSE]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 21],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {},
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 15],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.IRON, 1_000],
			[ResourceType.GOODS, 750],
			[ResourceType.STEEL, 500],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(150_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(25_000 / 4),
						[ResourceType.STONE]: ceilNumber(10_000 / 4),
						[ResourceType.CLAY]: ceilNumber(5_000 / 4),
						[ResourceType.IRON]: ceilNumber(2_500 / 4),
					},
				},
			],
		]),
		recommendedUpgrades: [28],
	},
	[FrameName.UNIVERSAL_WAREHOUSE]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 21],
			// [ResourceType.EARLY_ACCESS_LEVEL, 16],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {},
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 5],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.LUMBER, 25_000],
			[ResourceType.STONE, 12_500],
			[ResourceType.CLAY, 7_500],
			[ResourceType.IRON, 4_000],
			[ResourceType.GOODS, 3_000],
			[ResourceType.STEEL, 2_000],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
		recommendedUpgrades: [34],
	},
	// TU SOM SKONCIL, TREBA UPGRADE DOPLNIT
	[FrameName.TOOLMAKER]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 36],
			// [ResourceType.EARLY_ACCESS_LEVEL, 25],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {},
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.GOODS_INCOME,
				(): number => {
					const base = 40;
					const multiplier =
						1 +
						getUpgradeCurrentReplaceValueById(
							UpgradeIds.craftsmanship,
						);
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 75],
			[ResourceType.LUMBER_CONSUMPTION, 50],
			[ResourceType.IRON_CONSUMPTION, 10],
		]),
		storage: new Map<ResourceType, number>([[ResourceType.GOODS, 500]]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(250_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(50_000 / 4),
						[ResourceType.STONE]: ceilNumber(25_000 / 4),
						[ResourceType.CLAY]: ceilNumber(15_000 / 4),
						[ResourceType.IRON]: ceilNumber(10_500 / 4),
						[ResourceType.GOODS]: ceilNumber(2_500 / 4),
						[ResourceType.STEEL]: ceilNumber(1_000 / 4),
					},
				},
			],
		]),
		recommendedUpgrades: [51],
	},
	[FrameName.ARMORY]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 40],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {},
		production: new Map<ResourceType, number>([
			[ResourceType.PRODUCTION_RESOURCE_UNIT, 1],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 50],
			[ResourceType.GOODS_CONSUMPTION, 50],
			[ResourceType.STEEL_CONSUMPTION, 25],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(250_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(50_000 / 4),
						[ResourceType.STONE]: ceilNumber(35_000 / 4),
						[ResourceType.IRON]: ceilNumber(15_000 / 4),
						[ResourceType.STEEL]: ceilNumber(10_000 / 4),
					},
				},
			],
		]),
		supplies: new Set<FrameName>([FrameName.BARRACKS]),
	},
	[FrameName.BARRACKS]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 40],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {
			[ResourceType.ARMY_INCOME]: {
				value: 1,
			},
		},
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.ARMY_INCOME,
				(entity: TileEntity): number => {
					const base =
						1 *
						getProductionResourceUnit(
							entity.getOwnNeighbourTileEntities(),
							1,
						);
					const multiplier =
						1 +
						getProductionResourceMultiplier(
							entity.getOwnNeighbourTileEntities(),
						) +
						getUpgradeCurrentReplaceValueById(
							UpgradeIds.conscription,
						);
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 100],
		]),
		storage: new Map<ResourceType, number>([[ResourceType.ARMY, 50]]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(1_000_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(150_000 / 4),
						[ResourceType.STONE]: ceilNumber(75_000 / 4),
						[ResourceType.IRON]: ceilNumber(25_000 / 4),
						[ResourceType.STEEL]: ceilNumber(15_000 / 4),
					},
				},
			],
		]),
		range: 2, // cely settlement
		recommendedUpgrades: [47, 78],
	},
	[FrameName.OUTPOST]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 43],
			// [ResourceType.EARLY_ACCESS_LEVEL, 40],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {},
		production: new Map<ResourceType, ProductionValue>(),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 25],
		]),
		storage: new Map<ResourceType, number>([[ResourceType.ARMY, 200]]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(250_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(100_000 / 4),
						[ResourceType.STONE]: ceilNumber(50_000 / 4),
						[ResourceType.CLAY]: ceilNumber(35_000 / 4),
						[ResourceType.IRON]: ceilNumber(25_000 / 4),
						[ResourceType.STEEL]: ceilNumber(15_000 / 4),
						[ResourceType.ARMY]: ceilNumber(250 / 4),
					},
				},
			],
		]),
		recommendedUpgrades: [46],
	},
	[FrameName.HEALERS_HUT]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 33],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {},
		production: new Map<ResourceType, ProductionValue>([
			[ResourceType.PRODUCTION_RESOURCE_MULTIPLIER, 1.05],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 5],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(250_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(50_000 / 4),
						[ResourceType.STONE]: ceilNumber(25_000 / 4),
						[ResourceType.CLAY]: ceilNumber(15_000 / 4),
						[ResourceType.GOODS]: ceilNumber(10_000 / 4),
					},
				},
			],
		]),
		supplies: new Set<FrameName>([
			...tilerepoSettlementCastleBuildingsFrameNames,
			FrameName.SMALL_HOUSES,
			FrameName.MEDIUM_HOUSES,
			FrameName.LARGE_HOUSES,
			FrameName.CARPENTRY_DISTRICT,
			FrameName.STONEMASONRY_DISTRICT,
			FrameName.ENKINDLED_HOUSEHOLD,
		]),
	},
	[FrameName.HUNTERS_LODGE]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 29],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {
			[ResourceType.FOOD_INCOME]: {
				value: 5,
				frameName: FrameName.FOREST,
			},
		},
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.FOOD_INCOME,
				(entity: TileEntity): number => {
					const base =
						5 *
						getProductionResourceUnit(
							entity.getOwnNeighbourTerrainTileEntities("range"),
						);
					const multiplier =
						1 +
						getUpgradeCurrentReplaceValueById(UpgradeIds.tracking);
					return ceilNumber(base * multiplier);
				},
			],
			[
				ResourceType.PRODUCTION_RESOURCE_UNIT,
				(entity: TileEntity): number => {
					const base = getProductionResourceUnit(
						entity.getOwnNeighbourTerrainTileEntities("range"),
					);
					const multiplier = 1;
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 25],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(50_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(25_000 / 4),
						[ResourceType.IRON]: ceilNumber(2_500 / 4),
						[ResourceType.GOODS]: ceilNumber(1_500 / 4),
					},
				},
			],
		]),
		range: 1,
		supplies: new Set<FrameName>([FrameName.TANNERY]),
		recommendedUpgrades: [36, 63],
	},
	[FrameName.TANNERY]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 30],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {},
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.PRODUCTION_RESOURCE_UNIT,
				(entity: TileEntity): number => {
					const base = getProductionResourceUnit(
						entity.getOwnNeighbourTileEntities(),
					);
					const multiplier = 1;
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 10],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(30_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(10_000 / 4),
						[ResourceType.STONE]: ceilNumber(5_000 / 4),
						[ResourceType.CLAY]: ceilNumber(3_000 / 4),
					},
				},
			],
		]),
		range: 2,
		supplies: new Set<FrameName>([
			FrameName.TAILORS_WORKSHOP,
			FrameName.SHOEMAKER,
		]),
	},
	[FrameName.TAILORS_WORKSHOP]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 30],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {
			[ResourceType.GOODS_INCOME]: {
				value: 0.5,
				frameName: FrameName.FOREST,
			},
		},
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.GOODS_INCOME,
				(entity: TileEntity): number => {
					const base = getProductionResourceUnit(
						entity.getOwnNeighbourTileEntities(),
					);
					const multiplier = 0.5;
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 30],
		]),
		storage: new Map<ResourceType, number>([[ResourceType.GOODS, 300]]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(150_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(50_000 / 4),
						[ResourceType.STONE]: ceilNumber(25_000 / 4),
						[ResourceType.CLAY]: ceilNumber(15_000 / 4),
						[ResourceType.IRON]: ceilNumber(5_000 / 4),
						[ResourceType.GOODS]: ceilNumber(2_500 / 4),
					},
				},
			],
		]),
		range: 2,
	},
	[FrameName.LARGE_STONE_QUARRY]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 12],
			// [ResourceType.EARLY_ACCESS_LEVEL, 10],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {},
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.STONE_INCOME,
				(entity: TileEntity): number => {
					const base = 40;
					const oxenStables =
						entity.getOwnNeighbourTileEntitiesByNeighbourFrameName(
							FrameName.OXEN_STABLE,
							1,
						);
					const multiplier =
						1 +
						getProductionResourceMultiplier(oxenStables) +
						+getUpgradeCurrentReplaceValueById(UpgradeIds.pickaxe) +
						getUpgradeCurrentReplaceValueById(
							UpgradeIds.miners_guild,
						);
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 25],
		]),
		storage: new Map<ResourceType, number>([[ResourceType.STONE, 2_500]]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
		recommendedUpgrades: [41],
	},
	[FrameName.MEDIUM_HOUSES]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 18],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {},
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number => {
					const base =
						50 +
						getUpgradeCurrentReplaceValueById(UpgradeIds.attic2) +
						getUpgradeCurrentReplaceValueById(
							UpgradeIds.sanitation_system2,
						);
					const multiplier =
						1 +
						getProductionResourceMultiplier(
							entity.getOwnNeighbourTileEntities(),
						);
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 25],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(35_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(10_000 / 4),
						[ResourceType.STONE]: ceilNumber(5_000 / 4),
						[ResourceType.CLAY]: ceilNumber(1_500 / 4),
					},
				},
			],
			[
				FrameName.LARGE_HOUSES,
				{
					price: {
						[ResourceType.COINS]: 465_000,
						[ResourceType.LUMBER]: 140_000,
						[ResourceType.STONE]: 95_000,
						[ResourceType.CLAY]: 48_500,
						[ResourceType.IRON]: 50_000,
						[ResourceType.STEEL]: 20_000,
					},
				},
			],
			[
				FrameName.CARPENTRY_DISTRICT,
				{
					price: {
						[ResourceType.GEMS]:
							premiumBuildingsPrices[
								FrameName.CARPENTRY_DISTRICT
							],
					},
					reward: {
						[ResourceType.COINS]: ceilNumber(20_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(10_000 / 4),
						[ResourceType.STONE]: ceilNumber(5_000 / 4),
						[ResourceType.CLAY]: ceilNumber(1_500 / 4),
					},
				},
			],
			[
				FrameName.STONEMASONRY_DISTRICT,
				{
					price: {
						[ResourceType.GEMS]:
							premiumBuildingsPrices[
								FrameName.STONEMASONRY_DISTRICT
							],
					},
					reward: {
						[ResourceType.COINS]: ceilNumber(20_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(10_000 / 4),
						[ResourceType.STONE]: ceilNumber(5_000 / 4),
						[ResourceType.CLAY]: ceilNumber(1_500 / 4),
					},
				},
			],
		]),
		range: 2, // cely settlement
	},
	[FrameName.SILVER_MINE]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, OFFER_BUILDING_UNLOCK_LEVEL],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {},
		production: new Map<ResourceType, number>([
			[ResourceType.COINS_INCOME, 400],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 10],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
	},
	[FrameName.LIGHTHOUSE]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, OFFER_BUILDING_UNLOCK_LEVEL],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {},
		production: new Map<ResourceType, number>([
			[ResourceType.EXPLORATION_SPEED, 10],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
	},
	[FrameName.COUNTRY_HOUSE]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, OFFER_BUILDING_UNLOCK_LEVEL],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {},
		production: new Map<ResourceType, number>([
			[ResourceType.POPULATION_INCOME, 200],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 10],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
	},
	[FrameName.PUMPKIN_FARM]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, OFFER_BUILDING_UNLOCK_LEVEL],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {
			[ResourceType.FOOD_INCOME]: {
				value: 30,
				frameName: FrameName.PUMPKIN_FIELD,
			},
		},
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.FOOD_INCOME,
				(entity: TileEntity): number => {
					const base = getProductionResourceUnit(
						entity.getOwnNeighbourTileEntities(),
					);
					const multiplier = 30;
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 10],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
		range: 1,
	},
	[FrameName.BIG_SHIPYARD]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, OFFER_BUILDING_UNLOCK_LEVEL],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {},
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 20],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
	},
	[FrameName.FISHERMANS_HUT]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, OFFER_BUILDING_UNLOCK_LEVEL],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {},
		production: new Map<ResourceType, number>([
			[ResourceType.COINS_INCOME, 100],
			[ResourceType.FOOD_INCOME, 50],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 10],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
	},
	[FrameName.LARGE_CLAY_PIT]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 32],
			// [ResourceType.EARLY_ACCESS_LEVEL, 15],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {},
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.CLAY_INCOME,
				(entity: TileEntity): number => {
					const base = 30;
					const brickyards =
						entity.getOwnNeighbourTileEntitiesByNeighbourFrameName(
							FrameName.BRICKYARD,
							1,
						);
					const multiplier =
						1 +
						getProductionResourceMultiplier(brickyards) +
						getUpgradeCurrentReplaceValueById(
							UpgradeIds.beasts_of_burden,
						) +
						getUpgradeCurrentReplaceValueById(
							UpgradeIds.wheelbarrow,
						) +
						getUpgradeCurrentReplaceValueById(
							UpgradeIds.beasts_of_burden2,
						);
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 20],
		]),
		storage: new Map<ResourceType, number>([[ResourceType.CLAY, 1_500]]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
		recommendedUpgrades: [65],
	},
	[FrameName.GEMS_MINE]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 35],
			// [ResourceType.EARLY_ACCESS_LEVEL, 20],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {},
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(50_000_000 / 4),
					},
				},
			],
		]),
		recommendedUpgrades: [48],
	},
	[FrameName.LIVESTOCK_FARM]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 39],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {
			[ResourceType.FOOD_INCOME]: {
				value: 15,
				frameName: FrameName.CATTLE_RANGE,
			},
		},
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.FOOD_INCOME,
				(entity: TileEntity): number => {
					const base =
						15 *
						getProductionResourceUnit(
							entity.getOwnNeighbourTileEntities(),
						);
					const waterMills =
						entity.getOwnNeighbourTileEntitiesByNeighbourFrameName(
							FrameName.WATER_MILL,
							1,
						);
					const multiplier =
						1 +
						getProductionResourceMultiplier(waterMills) +
						getUpgradeCurrentReplaceValueById(UpgradeIds.grazing);
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 30],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(250_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(25_000 / 4),
						[ResourceType.STONE]: ceilNumber(10_000 / 4),
						[ResourceType.CLAY]: ceilNumber(5_000 / 4),
						[ResourceType.IRON]: ceilNumber(2_500 / 4),
						[ResourceType.GOODS]: ceilNumber(1_500 / 4),
					},
				},
			],
		]),
		range: 1,
		recommendedUpgrades: [52],
	},
	[FrameName.UNIVERSITY]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 38],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {},
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(): number => {
					const base = 125;
					const multiplier =
						1 +
						getUpgradeCurrentReplaceValueById(UpgradeIds.mushroom) +
						getUpgradeCurrentReplaceValueById(UpgradeIds.crater);
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 150],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(1_000_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(150_000 / 4),
						[ResourceType.STONE]: ceilNumber(75_000 / 4),
						[ResourceType.CLAY]: ceilNumber(50_000 / 4),
						[ResourceType.IRON]: ceilNumber(30_000 / 4),
						[ResourceType.GOODS]: ceilNumber(20_000 / 4),
						[ResourceType.STEEL]: ceilNumber(10_000 / 4),
					},
				},
			],
		]),
	},
	[FrameName.ADVANCED_IRON_MINE]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 37],
			// [ResourceType.EARLY_ACCESS_LEVEL, 20],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {},
		production: new Map<ResourceType, number>([
			[ResourceType.PRODUCTION_RESOURCE_UNIT, 2],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 40],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
		supplies: new Set<FrameName>([FrameName.FURNACE]),
	},
	[FrameName.HILLS_SCOUT_TOWER]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 42],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {},
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 10],
		]),
		storage: new Map<ResourceType, number>([[ResourceType.ARMY, 25]]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(150_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(150_000 / 4),
						[ResourceType.STONE]: ceilNumber(100_000 / 4),
						[ResourceType.CLAY]: ceilNumber(75_000 / 4),
						[ResourceType.IRON]: ceilNumber(50_000 / 4),
						[ResourceType.ARMY]: ceilNumber(100 / 4),
					},
				},
			],
		]),
		range: 4,
		recommendedUpgrades: [40],
	},
	[FrameName.BREWERY]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 45],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {},
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.PRODUCTION_RESOURCE_UNIT,
				(entity: TileEntity): number => {
					const base = getProductionResourceUnit(
						entity.getOwnNeighbourTileEntities(),
					);
					const multiplier = 1;
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 20],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(250_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(50_000 / 4),
						[ResourceType.STONE]: ceilNumber(25_000 / 4),
						[ResourceType.CLAY]: ceilNumber(100_000 / 4),
						[ResourceType.GOODS]: ceilNumber(25_000 / 4),
					},
				},
			],
		]),
		range: 1,
		supplies: new Set<FrameName>([FrameName.INN]),
		recommendedUpgrades: [66],
	},
	[FrameName.INN]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 45],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {
			[ResourceType.COINS_INCOME]: {
				value: 4,
				frameName: FrameName.HOPS_FIELD,
			},
		},
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(entity: TileEntity): number => {
					const base =
						4 *
						getProductionResourceUnit(
							entity.getOwnNeighbourTileEntities(),
						);
					const multiplier =
						1 +
						getUpgradeCurrentReplaceValueById(
							UpgradeIds.wine_cellar,
						);
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 100],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(1_000_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(250_000 / 4),
						[ResourceType.STONE]: ceilNumber(100_000 / 4),
						[ResourceType.CLAY]: ceilNumber(75_000 / 4),
						[ResourceType.GOODS]: ceilNumber(35_000 / 4),
					},
				},
			],
		]),
		range: 5,
	},
	[FrameName.SHOEMAKER]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 47],
			// [ResourceType.EARLY_ACCESS_LEVEL, 40],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {
			[ResourceType.GOODS_INCOME]: {
				value: 0.7,
				frameName: FrameName.FOREST,
			},
		},
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.GOODS_INCOME,
				(entity: TileEntity): number => {
					const base = getProductionResourceUnit(
						entity.getOwnNeighbourTileEntities(),
					);
					const multiplier = 0.7;
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 80],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(500_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(100_000 / 4),
						[ResourceType.STONE]: ceilNumber(75_000 / 4),
						[ResourceType.CLAY]: ceilNumber(50_000 / 4),
						[ResourceType.IRON]: ceilNumber(20_000 / 4),
						[ResourceType.STEEL]: ceilNumber(5_000 / 4),
					},
				},
			],
		]),
		range: 2,
	},
	[FrameName.PATISSERIE]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 48],
			// [ResourceType.EARLY_ACCESS_LEVEL, 35],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {},
		production: new Map<ResourceType, number>([
			[ResourceType.FOOD_INCOME, 250],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 10],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
	},
	[FrameName.GUARD_TOWER]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 50],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {},
		production: new Map<ResourceType, number>([
			[ResourceType.PRODUCTION_RESOURCE_MULTIPLIER, 1.1],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 60],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(1_500_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(100_000 / 4),
						[ResourceType.STONE]: ceilNumber(250_000 / 4),
						[ResourceType.IRON]: ceilNumber(50_000 / 4),
						[ResourceType.STEEL]: ceilNumber(25_000 / 4),
						[ResourceType.ARMY]: ceilNumber(1_500 / 4),
					},
				},
			],
		]),
		supplies: new Set<FrameName>([
			...tilerepoSettlementCastleBuildingsFrameNames,
			FrameName.SMALL_HOUSES,
			FrameName.MEDIUM_HOUSES,
			FrameName.LARGE_HOUSES,
			FrameName.CARPENTRY_DISTRICT,
			FrameName.STONEMASONRY_DISTRICT,
			FrameName.ENKINDLED_HOUSEHOLD,
		]),
	},
	[FrameName.CARPENTRY_DISTRICT]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 17],
			// [ResourceType.EARLY_ACCESS_LEVEL, 12],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {},
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number => {
					const base = 150;
					const multiplier =
						1 +
						getProductionResourceMultiplier(
							entity.getOwnNeighbourTileEntities(),
						);
					return ceilNumber(base * multiplier);
				},
			],
			[ResourceType.COINS_INCOME, 150],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 40],
			[ResourceType.LUMBER_CONSUMPTION, 50],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
		range: 2, // cely settlement
	},
	[FrameName.STONEMASONRY_DISTRICT]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 32],
			// [ResourceType.EARLY_ACCESS_LEVEL, 25],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {},
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number => {
					const base = 200;
					const multiplier =
						1 +
						getProductionResourceMultiplier(
							entity.getOwnNeighbourTileEntities(),
						);
					return ceilNumber(base * multiplier);
				},
			],
			[ResourceType.COINS_INCOME, 200],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 50],
			[ResourceType.STONE_CONSUMPTION, 50],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
		range: 2, // cely settlement
	},
	[FrameName.TAX_OFFICE]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 41],
			// [ResourceType.EARLY_ACCESS_LEVEL, 14],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {},
		production: new Map<ResourceType, number>([
			[ResourceType.PRODUCTION_ONCLICK_RESOURCE_MULTIPLIER, 1.5],
			[ResourceType.COINS_INCOME, 250],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 50],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
		supplies: new Set<FrameName>([
			...tilerepoSettlementCastleBuildingsFrameNames,
		]),
	},
	[FrameName.LARGE_HOUSES]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 44],
			// [ResourceType.EARLY_ACCESS_LEVEL, 23],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {},
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number => {
					const base =
						100 +
						getUpgradeCurrentReplaceValueById(UpgradeIds.attic3) +
						getUpgradeCurrentReplaceValueById(
							UpgradeIds.sanitation_system3,
						);
					const multiplier =
						1 +
						getProductionResourceMultiplier(
							entity.getOwnNeighbourTileEntities(),
						);
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 35],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(500_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(150_000 / 4),
						[ResourceType.STONE]: ceilNumber(100_000 / 4),
						[ResourceType.CLAY]: ceilNumber(50_000 / 4),
						[ResourceType.IRON]: ceilNumber(50_000 / 4),
						[ResourceType.STEEL]: ceilNumber(20_000 / 4),
					},
				},
			],
			[
				FrameName.CARPENTRY_DISTRICT,
				{
					price: {
						[ResourceType.GEMS]:
							premiumBuildingsPrices[
								FrameName.CARPENTRY_DISTRICT
							],
					},
					reward: {
						[ResourceType.COINS]: ceilNumber(20_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(10_000 / 4),
						[ResourceType.STONE]: ceilNumber(5_000 / 4),
						[ResourceType.CLAY]: ceilNumber(1_500 / 4),
					},
				},
			],
			[
				FrameName.STONEMASONRY_DISTRICT,
				{
					price: {
						[ResourceType.GEMS]:
							premiumBuildingsPrices[
								FrameName.STONEMASONRY_DISTRICT
							],
					},
					reward: {
						[ResourceType.COINS]: ceilNumber(20_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(10_000 / 4),
						[ResourceType.STONE]: ceilNumber(5_000 / 4),
						[ResourceType.CLAY]: ceilNumber(1_500 / 4),
					},
				},
			],
		]),
		range: 2, // cely settlement
	},
	[FrameName.ARTISAN_SMITH]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 46],
			// [ResourceType.EARLY_ACCESS_LEVEL, 30],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {},
		production: new Map<ResourceType, number>([
			[ResourceType.COINS_INCOME, 150],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 50],
			[ResourceType.IRON_CONSUMPTION, 25],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(250_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(100_000 / 4),
						[ResourceType.STONE]: ceilNumber(75_000 / 4),
						[ResourceType.IRON]: ceilNumber(20_000 / 4),
						[ResourceType.GOODS]: ceilNumber(10_000 / 4),
						[ResourceType.STEEL]: ceilNumber(5_000 / 4),
					},
				},
			],
		]),
	},
	[FrameName.WATER_SAW_MILL]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 49],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {
			[ResourceType.LUMBER_INCOME]: {
				value: 4,
				frameName: FrameName.FOREST,
			},
		},
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.LUMBER_INCOME,
				(entity: TileEntity): number => {
					const base =
						4 *
						getProductionResourceUnit(
							entity.getOwnNeighbourTerrainTileEntities("range"),
						);
					const multiplier =
						1 +
						getUpgradeCurrentReplaceValueById(UpgradeIds.bow_saw);
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 30],
		]),
		storage: new Map<ResourceType, number>([[ResourceType.LUMBER, 2_500]]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(300_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(25_000 / 4),
						[ResourceType.STONE]: ceilNumber(100_000 / 4),
						[ResourceType.CLAY]: ceilNumber(75_000 / 4),
						[ResourceType.IRON]: ceilNumber(25_000 / 4),
						[ResourceType.GOODS]: ceilNumber(15_000 / 4),
						[ResourceType.STEEL]: ceilNumber(15_000 / 4),
					},
				},
			],
		]),
		range: 1,
		recommendedUpgrades: [7],
	},
	[FrameName.FISHING_VILLAGE]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, OFFER_BUILDING_UNLOCK_LEVEL],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {},
		production: new Map<ResourceType, number>([
			[ResourceType.GOODS_INCOME, 50],
			[ResourceType.POPULATION_INCOME, 400],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 50],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
	},
	[FrameName.SCULPTOR]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, OFFER_BUILDING_UNLOCK_LEVEL],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {},
		production: new Map<ResourceType, number>([
			[ResourceType.COINS_INCOME, 2_000],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 75],
		]),
		storage: new Map<ResourceType, number>([[ResourceType.STONE, 10_000]]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
	},
	[FrameName.ROOF_TILE_FACTORY]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, OFFER_BUILDING_UNLOCK_LEVEL],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {},
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.CLAY_INCOME,
				(entity: TileEntity): number => {
					const base = 100;
					const brickyards =
						entity.getOwnNeighbourTileEntitiesByNeighbourFrameName(
							FrameName.BRICKYARD,
							1,
						);
					const multiplier =
						1 + getProductionResourceMultiplier(brickyards);
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 25],
		]),
		storage: new Map<ResourceType, number>([[ResourceType.CLAY, 5_000]]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
	},
	[FrameName.INDUSTRIAL_FOUNDRY]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 51],
			// [ResourceType.EARLY_ACCESS_LEVEL, 31],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {
			[ResourceType.STEEL_INCOME]: {
				value: 20,
			},
		},
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.STEEL_INCOME,
				(entity: TileEntity): number => {
					const base =
						entity.getOwnNeighbourTileEntities().length > 0
							? 20
							: 0;
					const multiplier =
						1 +
						getUpgradeCurrentReplaceValueById(UpgradeIds.smelting);
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 60],
			[ResourceType.IRON_CONSUMPTION, 10],
		]),
		storage: new Map<ResourceType, number>([[ResourceType.STEEL, 2_500]]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
		range: 2,
		recommendedUpgrades: [26],
	},
	[FrameName.ARENA]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 52],
			// [ResourceType.EARLY_ACCESS_LEVEL, 42],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {},
		production: new Map<ResourceType, number>([
			[ResourceType.PRODUCTION_RESOURCE_MULTIPLIER, 2],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 50],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(2_500_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(250_000 / 4),
						[ResourceType.STONE]: ceilNumber(150_000 / 4),
						[ResourceType.IRON]: ceilNumber(60_000 / 4),
						[ResourceType.GOODS]: ceilNumber(20_000 / 4),
						[ResourceType.STEEL]: ceilNumber(40_000 / 4),
						[ResourceType.ARMY]: ceilNumber(250 / 4),
					},
				},
			],
		]),
		supplies: new Set<FrameName>([
			FrameName.BARRACKS,
			FrameName.ARCHERY_RANGE,
			FrameName.STABLES,
			FrameName.ARMAMENTS_TRADER,
		]),
	},
	[FrameName.MOUNTAIN_BELLOWS]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 53],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {},
		production: new Map<ResourceType, number>([
			[ResourceType.IRON_INCOME, 50],
			[ResourceType.STEEL_INCOME, 15],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 200],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.IRON, 2_000],
			[ResourceType.STEEL, 1_000],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(10_000_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(500_000 / 4),
						[ResourceType.STONE]: ceilNumber(300_000 / 4),
						[ResourceType.CLAY]: ceilNumber(50_000 / 4),
						[ResourceType.IRON]: ceilNumber(100_000 / 4),
						[ResourceType.GOODS]: ceilNumber(50_000 / 4),
						[ResourceType.STEEL]: ceilNumber(100_000 / 4),
					},
				},
			],
		]),
	},
	[FrameName.WATER_MILL]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 55],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {},
		production: new Map<ResourceType, number>([
			[ResourceType.PRODUCTION_RESOURCE_MULTIPLIER, 1.75],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 50],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(500_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(50_000 / 4),
						[ResourceType.STONE]: ceilNumber(25_000 / 4),
						[ResourceType.CLAY]: ceilNumber(10_000 / 4),
						[ResourceType.IRON]: ceilNumber(7_500 / 4),
						[ResourceType.GOODS]: ceilNumber(5_000 / 4),
					},
				},
			],
		]),
		range: 3,
		supplies: new Set<FrameName>([
			FrameName.WHEAT_FARM,
			FrameName.LIVESTOCK_FARM,
		]),
		recommendedUpgrades: [61],
	},
	[FrameName.BRICKYARD]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 56],
			// [ResourceType.EARLY_ACCESS_LEVEL, 34],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {},
		production: new Map<ResourceType, number>([
			[ResourceType.PRODUCTION_RESOURCE_MULTIPLIER, 1.5],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 20],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
		range: 1,
		supplies: new Set<FrameName>([
			FrameName.CLAY_PIT,
			FrameName.LARGE_CLAY_PIT,
			FrameName.ROOF_TILE_FACTORY,
		]),
	},
	[FrameName.SHALLOW_SEA_PEARL_HUNTER]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 58],
			// [ResourceType.EARLY_ACCESS_LEVEL, 50],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {},
		production: new Map<ResourceType, ProductionValue>([
			[ResourceType.PRODUCTION_RESOURCE_MULTIPLIER, 1.25],
			[
				ResourceType.COINS_INCOME,
				(): number => {
					const base = 200;
					const multiplier =
						1 +
						getUpgradeCurrentReplaceValueById(UpgradeIds.snorkel) +
						getUpgradeCurrentReplaceValueById(UpgradeIds.atol);
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 50],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(750_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(100_000 / 4),
						[ResourceType.GOODS]: ceilNumber(25_000 / 4),
					},
				},
			],
		]),
		supplies: new Set<FrameName>([FrameName.RAFT]),
	},
	[FrameName.DEEP_SEA_PEARL_HUNTER]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 58],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {},
		production: new Map<ResourceType, ProductionValue>([
			[ResourceType.PRODUCTION_RESOURCE_MULTIPLIER, 1.25],
			[
				ResourceType.COINS_INCOME,
				(): number => {
					const base = 200;
					const multiplier =
						1 +
						getUpgradeCurrentReplaceValueById(UpgradeIds.snorkel) +
						getUpgradeCurrentReplaceValueById(UpgradeIds.atol);
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 50],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(750_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(100_000 / 4),
						[ResourceType.GOODS]: ceilNumber(25_000 / 4),
					},
				},
			],
		]),
		supplies: new Set<FrameName>([FrameName.RAFT]),
	},
	[FrameName.FLETCHER]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 60],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {},
		production: new Map<ResourceType, number>([
			[ResourceType.PRODUCTION_RESOURCE_UNIT, 1],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 50],
			[ResourceType.LUMBER_CONSUMPTION, 300],
			[ResourceType.STEEL_CONSUMPTION, 25],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(500_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(150_000 / 4),
						[ResourceType.STONE]: ceilNumber(75_000 / 4),
						[ResourceType.IRON]: ceilNumber(50_000 / 4),
						[ResourceType.STEEL]: ceilNumber(25_000 / 4),
					},
				},
			],
		]),
		supplies: new Set<FrameName>([FrameName.ARCHERY_RANGE]),
	},
	[FrameName.ARCHERY_RANGE]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 60],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {
			[ResourceType.ARMY_INCOME]: {
				value: 2,
			},
		},
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.ARMY_INCOME,
				(entity: TileEntity): number => {
					const base =
						2 *
						getProductionResourceUnit(
							entity.getOwnNeighbourTileEntities(),
							1,
						);
					const multiplier =
						1 +
						getProductionResourceMultiplier(
							entity.getOwnNeighbourTileEntities(),
						) +
						getUpgradeCurrentReplaceValueById(
							UpgradeIds.conscription2,
						);
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 100],
		]),
		storage: new Map<ResourceType, number>([[ResourceType.ARMY, 30]]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(1_500_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(250_000 / 4),
						[ResourceType.STONE]: ceilNumber(200_000 / 4),
						[ResourceType.IRON]: ceilNumber(100_000 / 4),
						[ResourceType.STEEL]: ceilNumber(50_000 / 4),
						[ResourceType.ARMY]: ceilNumber(2_500 / 4),
					},
				},
			],
		]),
		range: 2, // cely settlement
		recommendedUpgrades: [84, 87],
	},
	[FrameName.SEAWEED_FARM]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, OFFER_BUILDING_UNLOCK_LEVEL],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {
			[ResourceType.FOOD_INCOME]: {
				value: 40,
				frameName: FrameName.SEAWEED_FIELD,
			},
		},
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.FOOD_INCOME,
				(entity: TileEntity): number => {
					const base = getProductionResourceUnit(
						entity.getOwnNeighbourTileEntities(),
					);
					const multiplier = 40;
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 50],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
		range: 1,
	},
	[FrameName.TURKEY_FARM]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, OFFER_BUILDING_UNLOCK_LEVEL],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {},
		production: new Map<ResourceType, number>([
			[ResourceType.FOOD_INCOME, 500],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 25],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
	},
	[FrameName.MINT]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, OFFER_BUILDING_UNLOCK_LEVEL],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {},
		production: new Map<ResourceType, number>([
			[ResourceType.COINS_INCOME, 1_000],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 50],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
	},
	[FrameName.PORT]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 61],
			// [ResourceType.EARLY_ACCESS_LEVEL, 38],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {},
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 50],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.LUMBER, 7_500],
			[ResourceType.STONE, 3_750],
			[ResourceType.CLAY, 2_500],
			[ResourceType.IRON, 2_000],
			[ResourceType.GOODS, 1_500],
			[ResourceType.STEEL, 1_000],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(1_000_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(100_000 / 4),
						[ResourceType.STONE]: ceilNumber(75_000 / 4),
						[ResourceType.CLAY]: ceilNumber(50_000 / 4),
						[ResourceType.IRON]: ceilNumber(15_000 / 4),
						[ResourceType.GOODS]: ceilNumber(10_000 / 4),
						[ResourceType.STEEL]: ceilNumber(7_500 / 4),
						[ResourceType.ARMY]: ceilNumber(250 / 4),
					},
				},
			],
		]),
		range: 1,
	},
	[FrameName.BATH_HOUSE]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 63],
			// [ResourceType.EARLY_ACCESS_LEVEL, 50],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {},
		production: new Map<ResourceType, number>([
			[ResourceType.PRODUCTION_RESOURCE_MULTIPLIER, 1.1],
			[ResourceType.COINS_INCOME, 300],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 5],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
		supplies: new Set<FrameName>([
			...tilerepoSettlementCastleBuildingsFrameNames,
			FrameName.SMALL_HOUSES,
			FrameName.MEDIUM_HOUSES,
			FrameName.LARGE_HOUSES,
			FrameName.CARPENTRY_DISTRICT,
			FrameName.STONEMASONRY_DISTRICT,
			FrameName.ENKINDLED_HOUSEHOLD,
		]),
	},
	[FrameName.GOLD_PROSPECTOR]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 65],
			// [ResourceType.EARLY_ACCESS_LEVEL, 55],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {},
		production: new Map<ResourceType, number>([
			[ResourceType.PRODUCTION_RESOURCE_MULTIPLIER, 1.25],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 25],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(1_000_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(250_000 / 4),
						[ResourceType.IRON]: ceilNumber(150_000 / 4),
						[ResourceType.GOODS]: ceilNumber(25_000 / 4),
						[ResourceType.STEEL]: ceilNumber(75_000 / 4),
					},
				},
			],
		]),
		range: 1,
		supplies: new Set<FrameName>([
			FrameName.GOLD_MINE,
			FrameName.PREMIUM_GOLD_MINE,
		]),
	},
	[FrameName.DARKWOOD_LUMBER_MILL]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, EVENT_BUILDING_UNLOCK_LEVEL],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {
			[ResourceType.EVENTIUM_INCOME]: {
				value: 1,
				frameName: FrameName.FOREST,
			},
		},
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.EVENTIUM_INCOME,
				(entity: TileEntity): number => {
					// TODO CHRISTMAS: Restore the offer production bonus calculation.
					if (
						!isActiveEventProductionForEntity(
							(entity as EventTileEntity).eventType,
						)
					)
						return 0;

					const offerBonus = 0;
					// TODO: FE refactor
					// const eventPacks = useShopStore().eventPacks;
					// if (eventPacks) {
					// 	offerBonus = eventPacks.reduce(
					// 		(totalBonus: number, pack: ShopItem): number => {
					// 			const purchases = pack.num_of_purchases ?? 0;
					// 			if (purchases <= 0) return totalBonus;

					// 			const bonusPct = Object.values(
					// 				pack.attributes.rewards ?? {},
					// 			).reduce(
					// 				// TODO: DS - pories si typ pre rewardy prosim
					// 				// eslint-disable-next-line @typescript-eslint/no-explicit-any
					// 				(sum: number, reward: any): number =>
					// 					reward?.type === "income" &&
					// 					(reward as GameRewardIncome).currency ===
					// 						"eventium" &&
					// 					typeof (reward as GameRewardIncome).percentage ===
					// 						"number"
					// 						? sum + (reward as GameRewardIncome).percentage
					// 						: sum,
					// 				0,
					// 			);

					// 			return totalBonus + (bonusPct / 100) * purchases;
					// 		},
					// 		0,
					// 	);
					// }

					const base = getProductionResourceUnit(
						entity.getOwnNeighbourTerrainTileEntities("range"),
					);
					const multiplier =
						1 +
						offerBonus +
						getUpgradeCurrentReplaceValueById(
							EventUpgradeIds.ch_bow_saw,
						) +
						getUpgradeCurrentReplaceValueById(
							EventUpgradeIds.ch_two_man_saw,
						) +
						getUpgradeCurrentReplaceValueById(
							EventUpgradeIds.ch_wood_splitting,
						) +
						getUpgradeCurrentReplaceValueById(
							EventUpgradeIds.ch_wood_processing,
						);
					return ceilNumber(base * multiplier);
				},
			],
			[
				ResourceType.COINS_INCOME,
				(entity: TileEntity): number => {
					if (
						useUpgradesStore().getCurrentLevelById(
							EventUpgradeIds.ch_wood_carving,
						) === 0
					)
						return 0;

					const base = getProductionResourceUnit(
						entity.getOwnNeighbourTerrainTileEntities("range"),
					);
					const multiplier =
						0.5 *
						(1 +
							getUpgradeCurrentReplaceValueById(
								EventUpgradeIds.ch_bow_saw,
							) +
							getUpgradeCurrentReplaceValueById(
								EventUpgradeIds.ch_two_man_saw,
							) +
							getUpgradeCurrentReplaceValueById(
								EventUpgradeIds.ch_wood_splitting,
							) +
							getUpgradeCurrentReplaceValueById(
								EventUpgradeIds.ch_wood_processing,
							));
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
		range: 1,
	},
	[FrameName.TOYMAKERS_WORKSHOP]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, EVENT_BUILDING_UNLOCK_LEVEL],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {},
		production: new Map<ResourceType, ProductionValue>([
			[ResourceType.COINS_INCOME, 500],
			[ResourceType.GOODS_INCOME, 100],
			[ResourceType.POPULATION_INCOME, 100],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
	},
	[FrameName.APIARY]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 67],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {},
		production: new Map<ResourceType, number>([
			[ResourceType.FOOD_INCOME, 150],
			[ResourceType.PRODUCTION_RESOURCE_UNIT, 1],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 40],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(5_000_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(500_000 / 4),
						[ResourceType.STONE]: ceilNumber(250_000 / 4),
						[ResourceType.CLAY]: ceilNumber(75_000 / 4),
						[ResourceType.GOODS]: ceilNumber(150_000 / 4),
					},
				},
			],
		]),
		supplies: new Set<FrameName>([FrameName.CANDLEMAKER]),
	},
	[FrameName.CANDLEMAKER]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 67],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {
			[ResourceType.COINS_INCOME]: {
				value: 60,
			},
			[ResourceType.GOODS_INCOME]: {
				value: 30,
			},
		},
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(entity: TileEntity): number => {
					const base = getProductionResourceUnit(
						entity.getOwnNeighbourTileEntities(),
						1,
					);
					const isSettlementEmptyNearby = entity.tilerepo
						.getNeighbourTileIndexesByTileCoordinates(
							{
								x: entity.x,
								y: entity.y,
							},
							1,
						)
						.some((index: TileIndex): boolean => {
							const [tileId] =
								entity.tilerepo.getTileIdsByTileIndex(index);
							if (!tileId) return false;

							const frameName =
								entity.tilerepo.getFrameNameByTileId(tileId);
							if (!frameName) return false;

							return tilerepoSettlementEmptyFrameNames.has(
								frameName,
							);
						});
					const multiplier = 60 * (isSettlementEmptyNearby ? 1 : 0);
					return ceilNumber(base * multiplier);
				},
			],
			[
				ResourceType.GOODS_INCOME,
				(entity: TileEntity): number => {
					const base = getProductionResourceUnit(
						entity.getOwnNeighbourTileEntities(),
						1,
					);
					const isSettlementEmptyNearby = entity.tilerepo
						.getNeighbourTileIndexesByTileCoordinates(
							{
								x: entity.x,
								y: entity.y,
							},
							1,
						)
						.some((index: TileIndex): boolean => {
							const [tileId] =
								entity.tilerepo.getTileIdsByTileIndex(index);
							if (!tileId) return false;

							const frameName =
								entity.tilerepo.getFrameNameByTileId(tileId);
							if (!frameName) return false;

							return tilerepoSettlementEmptyFrameNames.has(
								frameName,
							);
						});
					const multiplier = 30 * (isSettlementEmptyNearby ? 1 : 0);
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 120],
		]),
		storage: new Map<ResourceType, number>([[ResourceType.GOODS, 800]]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(7_500_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(750_000 / 4),
						[ResourceType.STONE]: ceilNumber(400_000 / 4),
						[ResourceType.CLAY]: ceilNumber(200_000 / 4),
						[ResourceType.GOODS]: ceilNumber(200_000 / 4),
						[ResourceType.STEEL]: ceilNumber(75_000 / 4),
					},
				},
			],
		]),
		range: 1,
	},
	[FrameName.SADDLER]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 69],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {},
		production: new Map<ResourceType, number>([
			[ResourceType.PRODUCTION_RESOURCE_UNIT, 1],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 75],
			[ResourceType.GOODS_CONSUMPTION, 200],
			[ResourceType.STEEL_CONSUMPTION, 50],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(5_000_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(1_000_000 / 4),
						[ResourceType.STONE]: ceilNumber(750_000 / 4),
						[ResourceType.CLAY]: ceilNumber(500_000 / 4),
						[ResourceType.IRON]: ceilNumber(250_000 / 4),
						[ResourceType.GOODS]: ceilNumber(250_000 / 4),
						[ResourceType.STEEL]: ceilNumber(100_000 / 4),
					},
				},
			],
		]),
		supplies: new Set<FrameName>([FrameName.STABLES]),
		// range: 2,
	},
	[FrameName.STABLES]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 69],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {
			[ResourceType.ARMY_INCOME]: {
				value: 3,
			},
		},
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.ARMY_INCOME,
				(entity: TileEntity): number => {
					const base =
						3 *
						getProductionResourceUnit(
							entity.getOwnNeighbourTileEntities(),
							1,
						);
					const multiplier =
						1 +
						getProductionResourceMultiplier(
							entity.getOwnNeighbourTileEntities(),
						);
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 250],
		]),
		storage: new Map<ResourceType, number>([[ResourceType.ARMY, 20]]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(15_000_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(2_000_000 / 4),
						[ResourceType.STONE]: ceilNumber(1_000_000 / 4),
						[ResourceType.CLAY]: ceilNumber(750_000 / 4),
						[ResourceType.IRON]: ceilNumber(750_000 / 4),
						[ResourceType.GOODS]: ceilNumber(300_000 / 4),
						[ResourceType.STEEL]: ceilNumber(200_000 / 4),
						[ResourceType.ARMY]: ceilNumber(10_000 / 4),
					},
				},
			],
		]),
		range: 2, // cely settlement
		recommendedUpgrades: [],
	},
	[FrameName.TEMPLE]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 71],
			// [ResourceType.EARLY_ACCESS_LEVEL, 61],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
			[ResourceType.SETTLEMENT_LEVEL, 40],
		]),
		productionBase: {},
		production: new Map<ResourceType, number>([
			[ResourceType.COINS_INCOME, 500],
			[ResourceType.EXPLORATION_SPEED, 30],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 30],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
	},
	[FrameName.SHALLOW_SEA_BIG_FISHING_BOAT]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 73],
			// [ResourceType.EARLY_ACCESS_LEVEL, 60],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {
			[ResourceType.FOOD_INCOME]: {
				value: 4,
				frameName: FrameName.SHALLOW_SEA_SMALL_FISH,
			},
		},
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.FOOD_INCOME,
				(entity: TileEntity): number => {
					const base = getProductionResourceUnit(
						entity.getOwnNeighbourTerrainTileEntities("range"),
					);
					const multiplier = 2;
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 30],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
		range: 3,
		recommendedUpgrades: [],
	},
	[FrameName.DEEP_SEA_BIG_FISHING_BOAT]: {
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 73],
			// [ResourceType.EARLY_ACCESS_LEVEL, 60],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {
			[ResourceType.FOOD_INCOME]: {
				value: 4,
				frameName: FrameName.SHALLOW_SEA_SMALL_FISH,
			},
		},
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.FOOD_INCOME,
				(entity: TileEntity): number => {
					const base = getProductionResourceUnit(
						entity.getOwnNeighbourTerrainTileEntities("range"),
					);
					const multiplier = 2;
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 30],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
		range: 3,
		recommendedUpgrades: [],
	},
	[FrameName.APOTHECARY]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 75],
			// [ResourceType.EARLY_ACCESS_LEVEL, 65],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
			[ResourceType.SETTLEMENT_LEVEL, 40],
		]),
		productionBase: {},
		production: new Map<ResourceType, number>([
			[ResourceType.PRODUCTION_RESOURCE_MULTIPLIER, 1.1],
			[ResourceType.COINS_INCOME, 100],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 45],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(10_000_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(1_000_000 / 4),
						[ResourceType.STONE]: ceilNumber(750_000 / 4),
						[ResourceType.CLAY]: ceilNumber(250_000 / 4),
						[ResourceType.IRON]: ceilNumber(100_000 / 4),
						[ResourceType.GOODS]: ceilNumber(500_000 / 4),
					},
				},
			],
		]),
		supplies: new Set<FrameName>([
			...tilerepoSettlementCastleBuildingsFrameNames,
			FrameName.SMALL_HOUSES,
			FrameName.MEDIUM_HOUSES,
			FrameName.LARGE_HOUSES,
			FrameName.CARPENTRY_DISTRICT,
			FrameName.STONEMASONRY_DISTRICT,
			FrameName.ENKINDLED_HOUSEHOLD,
		]),
	},
	[FrameName.BANQUET]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, OFFER_BUILDING_UNLOCK_LEVEL],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {},
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.FOOD_INCOME,
				(entity: TileEntity): number => {
					const base = 200;
					const multiplier =
						1 +
						getProductionResourceMultiplier(
							entity.getOwnNeighbourTileEntities(),
							Infinity,
							true,
						);
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 10],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
		range: 1,
	},
	[FrameName.MINSTRELS_PODIUM]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, OFFER_BUILDING_UNLOCK_LEVEL],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {},
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(entity: TileEntity): number => {
					const base = 2_500;
					const multiplier =
						1 +
						getProductionResourceMultiplier(
							entity.getOwnNeighbourTileEntities(),
							Infinity,
							true,
						);
					return ceilNumber(base * multiplier);
				},
			],
			[ResourceType.PRODUCTION_RESOURCE_MULTIPLIER, 1.25],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 10],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
		range: 1,
		supplies: new Set<FrameName>([FrameName.BANQUET]),
	},
	[FrameName.MONOLITH_CIRCLE]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, OFFER_BUILDING_UNLOCK_LEVEL],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {},
		production: new Map<ResourceType, number>([
			[ResourceType.EXPLORATION_SPEED, 150],
			[ResourceType.PRODUCTION_RESOURCE_MULTIPLIER, 1.5],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 70],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
		range: 1,
		supplies: new Set<FrameName>([
			FrameName.BANQUET,
			FrameName.MINSTRELS_PODIUM,
		]),
	},
	[FrameName.RAFT]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, EVENT_BUILDING_UNLOCK_LEVEL],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {
			[ResourceType.EVENTIUM_INCOME]: {
				value: 2,
				frameName: FrameName.SHALLOW_SEA_SMALL_FISH,
			},
		},
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.EVENTIUM_INCOME,
				(entity: TileEntity): number => {
					if (
						!isActiveEventProductionForEntity(
							(entity as EventTileEntity).eventType,
						)
					)
						return 0;

					const base = getProductionResourceUnit(
						entity.getOwnNeighbourTerrainTileEntities("range"),
					);
					const eventMultiplier =
						getUpgradeCurrentReplaceValueById(
							EventUpgradeIds.fi_fish_feeding,
						) > 0
							? getProductionResourceMultiplier(
									entity.getOwnNeighbourTileEntities(),
									1,
								)
							: 0;
					const multiplier =
						eventMultiplier +
						1 +
						getUpgradeCurrentReplaceValueById(
							EventUpgradeIds.fi_trained_fishermen,
						) +
						getUpgradeCurrentReplaceValueById(
							EventUpgradeIds.fi_experienced_fishermen,
						) +
						getUpgradeCurrentReplaceValueById(
							EventUpgradeIds.fi_professional_fishermen,
						) +
						getUpgradeCurrentReplaceValueById(
							EventUpgradeIds.fi_elite_fishermen,
						) +
						getUpgradeCurrentReplaceValueById(
							EventUpgradeIds.fi_eventium_income,
						) +
						getUpgradeCurrentReplaceValueById(
							EventUpgradeIds.fi_eventium_income2,
						);
					return ceilNumber(base * multiplier);
				},
			],
			[
				ResourceType.COINS_INCOME,
				(entity: TileEntity): number => {
					if (
						!isActiveEventProductionForEntity(
							(entity as EventTileEntity).eventType,
						)
					)
						return 0;

					const base = getProductionResourceUnit(
						entity.getOwnNeighbourTerrainTileEntities("range"),
					);
					const eventMultiplier =
						getUpgradeCurrentReplaceValueById(
							EventUpgradeIds.fi_fish_feeding,
						) > 0
							? getProductionResourceMultiplier(
									entity.getOwnNeighbourTileEntities(),
									1,
								)
							: 0;
					const multiplier =
						getUpgradeCurrentReplaceValueById(
							EventUpgradeIds.fi_fish_market,
						) *
						(eventMultiplier +
							1 +
							getUpgradeCurrentReplaceValueById(
								EventUpgradeIds.fi_trained_fishermen,
							) +
							getUpgradeCurrentReplaceValueById(
								EventUpgradeIds.fi_experienced_fishermen,
							) +
							getUpgradeCurrentReplaceValueById(
								EventUpgradeIds.fi_professional_fishermen,
							) +
							getUpgradeCurrentReplaceValueById(
								EventUpgradeIds.fi_elite_fishermen,
							) +
							getUpgradeCurrentReplaceValueById(
								EventUpgradeIds.fi_eventium_income,
							) +
							getUpgradeCurrentReplaceValueById(
								EventUpgradeIds.fi_eventium_income2,
							));
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
		range: 2,
	},
	[FrameName.BOTTLETRAP]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, EVENT_BUILDING_UNLOCK_LEVEL],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {
			[ResourceType.EVENTIUM_INCOME]: {
				value: 3,
				frameName: FrameName.RIVER3,
			},
		},
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.EVENTIUM_INCOME,
				(entity: TileEntity): number => {
					if (
						!isActiveEventProductionForEntity(
							(entity as EventTileEntity).eventType,
						)
					)
						return 0;

					const base =
						3 *
						getProductionResourceUnit(
							entity.getOwnNeighbourTerrainTileEntities("range"),
						);
					const multiplier =
						1 +
						getUpgradeCurrentReplaceValueById(
							EventUpgradeIds.fi_bait,
						) +
						getUpgradeCurrentReplaceValueById(
							EventUpgradeIds.fi_flax_cage,
						) +
						getUpgradeCurrentReplaceValueById(
							EventUpgradeIds.fi_special_bait,
						) +
						getUpgradeCurrentReplaceValueById(
							EventUpgradeIds.fi_wire_cage,
						) +
						getUpgradeCurrentReplaceValueById(
							EventUpgradeIds.fi_eventium_income,
						) +
						getUpgradeCurrentReplaceValueById(
							EventUpgradeIds.fi_eventium_income2,
						);
					return ceilNumber(base * multiplier);
				},
			],
			[
				ResourceType.COINS_INCOME,
				(entity: TileEntity): number => {
					if (
						!isActiveEventProductionForEntity(
							(entity as EventTileEntity).eventType,
						)
					)
						return 0;

					const base =
						3 *
						getProductionResourceUnit(
							entity.getOwnNeighbourTerrainTileEntities("range"),
						);
					const multiplier =
						getUpgradeCurrentReplaceValueById(
							EventUpgradeIds.fi_fish_market,
						) *
						(1 +
							getUpgradeCurrentReplaceValueById(
								EventUpgradeIds.fi_bait,
							) +
							getUpgradeCurrentReplaceValueById(
								EventUpgradeIds.fi_flax_cage,
							) +
							getUpgradeCurrentReplaceValueById(
								EventUpgradeIds.fi_special_bait,
							) +
							getUpgradeCurrentReplaceValueById(
								EventUpgradeIds.fi_wire_cage,
							) +
							getUpgradeCurrentReplaceValueById(
								EventUpgradeIds.fi_eventium_income,
							) +
							getUpgradeCurrentReplaceValueById(
								EventUpgradeIds.fi_eventium_income2,
							));
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
		range: 2,
	},
	[FrameName.FORTRESS]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, EVENT_BUILDING_UNLOCK_LEVEL],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {},
		production: new Map<ResourceType, ProductionValue>([
			[ResourceType.ARMY_INCOME, 1],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>([
			[ResourceType.LUMBER, 30_000],
			[ResourceType.STONE, 15_000],
			[ResourceType.CLAY, 9_000],
			[ResourceType.IRON, 5_000],
			[ResourceType.GOODS, 3_500],
			[ResourceType.STEEL, 2_500],
			[ResourceType.ARMY, 1_000],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
	},
	[FrameName.RABBIT_FARM]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, EVENT_BUILDING_UNLOCK_LEVEL],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {},
		production: new Map<ResourceType, number>([
			[ResourceType.COINS_INCOME, 200],
			[ResourceType.FOOD_INCOME, 250],
			[ResourceType.PRODUCTION_RESOURCE_UNIT, 50],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 15],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
		supplies: new Set<FrameName>([FrameName.TANNERY]),
	},
	[FrameName.HAUNTED_MANSION]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, EVENT_BUILDING_UNLOCK_LEVEL],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {
			[ResourceType.COINS_INCOME]: {
				value: 75,
				frameName: FrameName.OLD_FOREST,
			},
		},
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(entity: TileEntity): number => {
					const base =
						15 *
						getProductionResourceUnit(
							entity.getOwnNeighbourTerrainTileEntities("range"),
						);
					return ceilNumber(base);
				},
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 30],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
		range: 1,
	},
	[FrameName.CORSAIR_SHIP]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, EVENT_BUILDING_UNLOCK_LEVEL],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {},
		production: new Map<ResourceType, number>([
			[ResourceType.COINS_INCOME, 400],
			[ResourceType.GOODS_INCOME, 100],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 30],
		]),
		storage: new Map<ResourceType, number>([[ResourceType.ARMY, 100]]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
	},
	[FrameName.LUCKY_CAULDRON]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, OFFER_BUILDING_UNLOCK_LEVEL],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {},
		production: new Map<ResourceType, number>([
			[ResourceType.COINS_INCOME, 500],
			[ResourceType.FOOD_INCOME, 1_500],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 10],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
	},
	[FrameName.HILLS_BLAST_FURNACE]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, OFFER_BUILDING_UNLOCK_LEVEL],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.IRON_INCOME,
				(): number => {
					const base = 200;
					const multiplier = 1;
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 75],
		]),
		storage: new Map<ResourceType, number>([[ResourceType.IRON, 5_000]]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
		supplies: new Set<FrameName>([
			FrameName.FOUNDRY,
			FrameName.INDUSTRIAL_FOUNDRY,
		]),
	},
	[FrameName.STORAGE_OVERSEER]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, OFFER_BUILDING_UNLOCK_LEVEL],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		production: new Map<ResourceType, ProductionValue>([
			[ResourceType.STORAGE_RESOURCE_MULTIPLIER, 1.5],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 10],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
		range: 1,
		supplies: new Set<FrameName>([
			FrameName.SMALL_STOCKPILE,
			FrameName.WAREHOUSE,
			FrameName.UNIVERSAL_WAREHOUSE,
			FrameName.PORT,
			FrameName.FORTRESS,
			FrameName.GREAT_WAREHOUSE,
		]),
	},
	[FrameName.ARMAMENTS_TRADER]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, OFFER_BUILDING_UNLOCK_LEVEL],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		production: new Map<ResourceType, ProductionValue>([
			[ResourceType.PRODUCTION_RESOURCE_UNIT, 1],
			[
				ResourceType.ARMY_INCOME,
				(entity: TileEntity): number => {
					const base = 2;
					const multiplier =
						1 +
						getProductionResourceMultiplier(
							entity.getOwnNeighbourTileEntities(),
						);
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 100],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
		range: 2, // cely settlement
		supplies: new Set<FrameName>([
			FrameName.BARRACKS,
			FrameName.ARCHERY_RANGE,
			FrameName.STABLES,
		]),
	},
	[FrameName.HILLS_LOOKOUT_TOWER]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, OFFER_BUILDING_UNLOCK_LEVEL],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {},
		production: new Map<ResourceType, number>([
			[ResourceType.EXPLORATION_SPEED, 100],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 10],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
	},
	[FrameName.PEA_PATCH]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, OFFER_BUILDING_UNLOCK_LEVEL],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {},
		production: new Map<ResourceType, number>([
			[ResourceType.FOOD_INCOME, 400],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 10],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
	},
	[FrameName.MANOR]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, OFFER_BUILDING_UNLOCK_LEVEL],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {},
		production: new Map<ResourceType, number>([
			[ResourceType.POPULATION_INCOME, 600],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.FOOD_CONSUMPTION, 30],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
	},
	[FrameName.LARGE_BREWERY]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, OFFER_BUILDING_UNLOCK_LEVEL],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {},
		production: new Map<ResourceType, ProductionValue>([
			[ResourceType.FOOD_INCOME, 100],
			[
				ResourceType.PRODUCTION_RESOURCE_UNIT,
				(entity: TileEntity): number => {
					const base =
						5 *
						getProductionResourceUnit(
							entity.getOwnNeighbourTileEntities(),
						);
					const multiplier = 1;
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 10],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
		range: 1,
		supplies: new Set<FrameName>([FrameName.INN]),
	},
	[FrameName.ENKINDLED_HOUSEHOLD]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, EVENT_BUILDING_UNLOCK_LEVEL],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {},
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.POPULATION_INCOME,
				(entity: TileEntity): number => {
					const base = 300;
					const multiplier =
						1 +
						getProductionResourceMultiplier(
							entity.getOwnNeighbourTileEntities(),
						);
					return ceilNumber(base * multiplier);
				},
			],
			[ResourceType.COINS_INCOME, 400],
			[ResourceType.LUMBER_INCOME, 200],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
		range: 2, // cely settlement
	},
	[FrameName.FIRE_FESTIVAL_PYRE]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, EVENT_BUILDING_UNLOCK_LEVEL],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.EVENTIUM_INCOME,
				(entity: TileEntity): number => {
					if (
						!isActiveEventProductionForEntity(
							(entity as EventTileEntity).eventType,
						)
					)
						return 0;

					const { minLevel } = useSettlementsStore();

					const base = 5 + Math.trunc(minLevel / 10);

					const upgradesMultiplierIds = [
						EventUpgradeIds.ff_apple_wood,
						EventUpgradeIds.ff_cherry_wood,
						EventUpgradeIds.ff_birch_wood,
						EventUpgradeIds.ff_hickory_wood,
						EventUpgradeIds.ff_special_04,
						EventUpgradeIds.ff_bundle_1,
						EventUpgradeIds.ff_bundle_2,
						EventUpgradeIds.ff_bundle_3,
						EventUpgradeIds.ff_bundle_4,
					];

					const multiplier =
						1 +
						upgradesMultiplierIds.reduce(
							(sum: number, id: EventUpgradeIds): number => {
								return (
									sum + getUpgradeCurrentReplaceValueById(id)
								);
							},
							0,
						);

					const baseWoodIds = [
						EventUpgradeIds.ff_maple_wood,
						EventUpgradeIds.ff_beech_wood,
						EventUpgradeIds.ff_black_locust_wood,
						EventUpgradeIds.ff_oak_wood,
					];

					const specialWoodIds = [
						EventUpgradeIds.ff_cleft_wood,
						EventUpgradeIds.ff_seasoned_wood,
						EventUpgradeIds.ff_special_01,
						EventUpgradeIds.ff_special_03,
					];

					let upgradeIncrease = baseWoodIds.reduce(
						(sum: number, id: EventUpgradeIds): number =>
							sum + getUpgradeCurrentReplaceValueById(id),
						0,
					);

					const hasSpecialUpgrade = specialWoodIds.some(
						(id: EventUpgradeIds): boolean =>
							getUpgradeCurrentReplaceValueById(id) > 0,
					);

					if (hasSpecialUpgrade) {
						const count = Math.floor(
							useSettlementsStore().getLowestSettlementLevel() /
								10,
						);

						const totalSpecialValues = specialWoodIds.reduce(
							(sum: number, id: EventUpgradeIds): number =>
								sum + getUpgradeCurrentReplaceValueById(id),
							0,
						);

						upgradeIncrease += count * totalSpecialValues;
					}

					return ceilNumber((base + upgradeIncrease) * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
		range: 2,
	},
	[FrameName.BOUNTIFUL_MARKETPLACE]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, EVENT_BUILDING_UNLOCK_LEVEL],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {},
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.EVENTIUM_INCOME,
				(entity: TileEntity): number => {
					if (
						!isActiveEventProductionForEntity(
							(entity as EventTileEntity).eventType,
						)
					)
						return 0;

					const neighbourTileIndexes =
						entity.tilerepo.getNeighbourTileIndexesByTileCoordinates(
							{
								x: entity.x,
								y: entity.y,
							},
							entity.range!,
						);

					let accumulator = 0;

					for (const neighbourTileIndex of neighbourTileIndexes) {
						const neighbourEntity =
							entity.tilerepo.getTileEntityByTileIndex(
								neighbourTileIndex,
							);
						if (!neighbourEntity) continue;

						switch (neighbourEntity.frameName) {
							case FrameName.GATHERERS_HUT:
							case FrameName.WHEAT_FARM:
							case FrameName.WHEAT_FIELD:
							case FrameName.SHALLOW_SEA_FISHING_BOAT:
							case FrameName.DEEP_SEA_FISHING_BOAT: {
								accumulator += 1;
								break;
							}
							case FrameName.BAKERY:
							case FrameName.SHALLOW_SEA_SMALL_FISH_TRAP:
							case FrameName.SHALLOW_SEA_MEDIUM_FISH_TRAP:
							case FrameName.HUNTERS_LODGE:
							case FrameName.LIVESTOCK_FARM:
							case FrameName.CATTLE_RANGE:
							case FrameName.CATTLE_RANGE_SPECIAL:
							case FrameName.APIARY:
							case FrameName.SHALLOW_SEA_BIG_FISHING_BOAT:
							case FrameName.DEEP_SEA_BIG_FISHING_BOAT: {
								accumulator += 2;
								break;
							}
							case FrameName.PATISSERIE:
							case FrameName.PUMPKIN_FARM:
							case FrameName.PUMPKIN_FIELD:
							case FrameName.FISHERMANS_HUT:
							case FrameName.SEAWEED_FARM:
							case FrameName.SEAWEED_FIELD:
							case FrameName.PEA_PATCH:
							case FrameName.LARGE_BREWERY:
							case FrameName.TURKEY_FARM:
							case FrameName.BANQUET:
							case FrameName.LUCKY_CAULDRON:
							case FrameName.RABBIT_FARM:
							case FrameName.SOLSTICE_WINDMILL: {
								accumulator += 3;
								break;
							}
						}
					}

					const upgradesMultiplierIds = [
						EventUpgradeIds.sf_skilled_gatherers,
						EventUpgradeIds.sf_adept_hunters,
						EventUpgradeIds.sf_experienced_farmers,
						EventUpgradeIds.sf_master_bakers,
						EventUpgradeIds.sf_bundle_1,
						EventUpgradeIds.sf_bundle_2,
						EventUpgradeIds.sf_bundle_3,
					];

					const multiplier =
						1 +
						upgradesMultiplierIds.reduce(
							(sum: number, id: EventUpgradeIds): number => {
								return (
									sum + getUpgradeCurrentReplaceValueById(id)
								);
							},
							0,
						);

					const upgradesIncreaseIds = [
						EventUpgradeIds.sf_good_harvest,
						EventUpgradeIds.sf_great_harvest,
						EventUpgradeIds.sf_bountiful_harvest,
						EventUpgradeIds.sf_historic_harvest,
						EventUpgradeIds.sf_special_01,
						EventUpgradeIds.sf_special_03,
					];

					const upgradeIncrease = upgradesIncreaseIds.reduce(
						(sum: number, id: EventUpgradeIds): number =>
							sum + getUpgradeCurrentReplaceValueById(id),
						0,
					);

					return ceilNumber(
						(accumulator + upgradeIncrease) * multiplier,
					);
				},
			],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
		range: 3,
	},
	[FrameName.SOLSTICE_WINDMILL]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, EVENT_BUILDING_UNLOCK_LEVEL],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {
			[ResourceType.FOOD_INCOME]: {
				value: 10,
				frameName: FrameName.WHEAT_FIELD,
			},
		},
		production: new Map<ResourceType, ProductionValue>([
			[ResourceType.COINS_INCOME, 400],
			[
				ResourceType.FOOD_INCOME,
				(entity: TileEntity): number => {
					const base =
						10 *
						getProductionResourceUnit(
							entity.getOwnNeighbourTileEntities(),
						);
					const multiplier = 1;
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
		range: 3,
	},
	[FrameName.HUNTING_CAMP]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, EVENT_BUILDING_UNLOCK_LEVEL],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {
			[ResourceType.EVENTIUM_INCOME]: {
				value: 1,
				frameName: FrameName.FOREST,
			},
		},
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.EVENTIUM_INCOME,
				(entity: TileEntity): number => {
					if (
						!isActiveEventProductionForEntity(
							(entity as EventTileEntity).eventType,
						)
					)
						return 0;

					const base = getProductionResourceUnit(
						entity.getOwnNeighbourTerrainTileEntities("range"),
					);

					const upgradesMultiplierIds = [
						EventUpgradeIds.hu_duck_season,
						EventUpgradeIds.hu_rabbit_season,
						EventUpgradeIds.hu_boar_season,
						EventUpgradeIds.hu_red_deer_season,
						EventUpgradeIds.hu_bow_1,
						EventUpgradeIds.hu_bow_3,
						EventUpgradeIds.hu_horse_2,
						EventUpgradeIds.hu_horse_4,
					];

					const multiplier =
						1 +
						upgradesMultiplierIds.reduce(
							(sum: number, id: EventUpgradeIds): number => {
								return (
									sum + getUpgradeCurrentReplaceValueById(id)
								);
							},
							0,
						);
					return ceilNumber(base * multiplier);
				},
			],
			[
				ResourceType.COINS_INCOME,
				(entity: TileEntity): number => {
					if (
						!isActiveEventProductionForEntity(
							(entity as EventTileEntity).eventType,
						)
					)
						return 0;

					const base = getProductionResourceUnit(
						entity.getOwnNeighbourTerrainTileEntities("range"),
					);

					const upgradesMultiplierIds = [
						EventUpgradeIds.hu_duck_season,
						EventUpgradeIds.hu_rabbit_season,
						EventUpgradeIds.hu_boar_season,
						EventUpgradeIds.hu_red_deer_season,
						EventUpgradeIds.hu_bow_1,
						EventUpgradeIds.hu_bow_3,
						EventUpgradeIds.hu_horse_2,
						EventUpgradeIds.hu_horse_4,
					];

					const multiplier =
						getUpgradeCurrentReplaceValueById(
							EventUpgradeIds.hu_trophy_exhibits,
						) *
						(1 +
							upgradesMultiplierIds.reduce(
								(sum: number, id: EventUpgradeIds): number => {
									return (
										sum +
										getUpgradeCurrentReplaceValueById(id)
									);
								},
								0,
							));

					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
		range: 1,
	},
	[FrameName.HIGHLAND_HUNTER]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, EVENT_BUILDING_UNLOCK_LEVEL],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {
			[ResourceType.EVENTIUM_INCOME]: {
				value: 5,
				frameName: FrameName.HILLS,
			},
		},
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.EVENTIUM_INCOME,
				(entity: TileEntity): number => {
					if (
						!isActiveEventProductionForEntity(
							(entity as EventTileEntity).eventType,
						)
					)
						return 0;

					const base =
						5 *
						entity.getOwnNeighbourTerrainTileEntities("range")
							.length;

					const upgradesMultiplierIds = [
						EventUpgradeIds.hu_fox_season,
						EventUpgradeIds.hu_deer_season,
						EventUpgradeIds.hu_wolf_season,
						EventUpgradeIds.hu_bear_season,
						EventUpgradeIds.hu_bow_1,
						EventUpgradeIds.hu_bow_3,
						EventUpgradeIds.hu_horse_2,
						EventUpgradeIds.hu_horse_4,
					];

					const multiplier =
						1 +
						upgradesMultiplierIds.reduce(
							(sum: number, id: EventUpgradeIds): number => {
								return (
									sum + getUpgradeCurrentReplaceValueById(id)
								);
							},
							0,
						);
					return ceilNumber(base * multiplier);
				},
			],
			[
				ResourceType.COINS_INCOME,
				(entity: TileEntity): number => {
					if (
						!isActiveEventProductionForEntity(
							(entity as EventTileEntity).eventType,
						)
					)
						return 0;

					const base =
						5 *
						entity.getOwnNeighbourTerrainTileEntities("range")
							.length;

					const upgradesMultiplierIds = [
						EventUpgradeIds.hu_fox_season,
						EventUpgradeIds.hu_deer_season,
						EventUpgradeIds.hu_wolf_season,
						EventUpgradeIds.hu_bear_season,
						EventUpgradeIds.hu_bow_1,
						EventUpgradeIds.hu_bow_3,
						EventUpgradeIds.hu_horse_2,
						EventUpgradeIds.hu_horse_4,
					];

					const multiplier =
						getUpgradeCurrentReplaceValueById(
							EventUpgradeIds.hu_trophy_exhibits,
						) *
						(1 +
							upgradesMultiplierIds.reduce(
								(sum: number, id: EventUpgradeIds): number => {
									return (
										sum +
										getUpgradeCurrentReplaceValueById(id)
									);
								},
								0,
							));

					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
		range: 1,
	},
	[FrameName.SHEPHERDS_LODGE]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, EVENT_BUILDING_UNLOCK_LEVEL],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {},
		production: new Map<ResourceType, ProductionValue>([
			[ResourceType.FOOD_INCOME, 250],
			[ResourceType.COINS_INCOME, 200],
			[ResourceType.GOODS_INCOME, 100],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 15],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
	},
	[FrameName.GREAT_WAREHOUSE]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, OFFER_BUILDING_UNLOCK_LEVEL],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {},
		production: new Map<ResourceType, ProductionValue>(),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 40],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.LUMBER, 200_000],
			[ResourceType.STONE, 100_000],
			[ResourceType.CLAY, 50_000],
			[ResourceType.IRON, 40_000],
			[ResourceType.GOODS, 30_000],
			[ResourceType.STEEL, 25_000],
			[ResourceType.ARMY, 2_500],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
	},
	[FrameName.APPLE_ORCHARD]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, OFFER_BUILDING_UNLOCK_LEVEL],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {},
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.FOOD_INCOME,
				(entity: TileEntity): number => {
					const base =
						100 *
						getProductionResourceUnit(
							entity.getOwnNeighbourTileEntities(),
						);
					const multiplier = 1;
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 50],
		]),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
		range: 2,
	},
	[FrameName.FORESTER_COTTAGE]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, OFFER_BUILDING_UNLOCK_LEVEL],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {},
		production: new Map<ResourceType, ProductionValue>([
			[ResourceType.LUMBER_INCOME, 300],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 5],
		]),
		storage: new Map<ResourceType, number>([[ResourceType.LUMBER, 10_000]]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
	},
	[FrameName.MILITARY_SHIPYARD]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 72],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {},
		production: new Map<ResourceType, ProductionValue>([
			[ResourceType.ARMY_INCOME, 1],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 300],
		]),
		storage: new Map<ResourceType, number>([
			[ResourceType.ARMY, 250],
			[ResourceType.RATIONS, 50],
		]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(25_000_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(2_500_000 / 4),
						[ResourceType.STONE]: ceilNumber(1_250_000 / 4),
						[ResourceType.CLAY]: ceilNumber(1_000_000 / 4),
						[ResourceType.IRON]: ceilNumber(1_000_000 / 4),
						[ResourceType.GOODS]: ceilNumber(500_000 / 4),
						[ResourceType.STEEL]: ceilNumber(350_000 / 4),
						[ResourceType.ARMY]: ceilNumber(15_000 / 4),
					},
				},
			],
		]),
	},
	[FrameName.RATIONS_SUPPLIER]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 44],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {},
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.RATIONS_INCOME,
				(): number => {
					const base = 1;
					const multiplier = 1;
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 150],
			[ResourceType.FOOD_CONSUMPTION, 2_500],
		]),
		storage: new Map<ResourceType, number>([[ResourceType.RATIONS, 750]]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(5_000_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(500_000 / 4),
						[ResourceType.STONE]: ceilNumber(300_000 / 4),
						[ResourceType.IRON]: ceilNumber(150_000 / 4),
						[ResourceType.GOODS]: ceilNumber(200_000 / 4),
						[ResourceType.STEEL]: ceilNumber(100_000 / 4),
						[ResourceType.ARMY]: ceilNumber(1_500 / 4),
					},
				},
			],
		]),
	},
	[FrameName.MILITARY_ACADEMY]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 57],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		productionBase: {},
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.RATIONS_INCOME,
				(): number => {
					const base = 2;
					const multiplier = 1;
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>([
			[ResourceType.POPULATION_CONSUMPTION, 100],
			[ResourceType.FOOD_CONSUMPTION, 2_000],
		]),
		storage: new Map<ResourceType, number>([[ResourceType.RATIONS, 1_000]]),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					// Budova ide do storage.
				},
			],
		]),
	},
	[FrameName.MILITIA]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 44],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {},
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: 1_000_000,
						[ResourceType.IRON]: 25_000,
						[ResourceType.ARMY]: 500,
						[ResourceType.RATIONS]: 250,
					},
				},
			],
		]),
		siegeAttributes: {
			maxHp: 750,
			armor: 1,
			attack: {
				melee: 1,
				ranged: 0,
				siege: 0,
			},
			supplyLimit: -1,
			attackClicks: 2,
			grantBuffs: [
				{
					type: BuffType.ENGAGEMENT_ATTACK,
					value: 1,
				},
			],
			buildingLimitFrameName: FrameName.BARRACKS,
		},
		action: TileAction.DISBAND,
	},
	[FrameName.SUPPLY_CART]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 44],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {},
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: 2_500_000,
						[ResourceType.ARMY]: 1_000,
						[ResourceType.RATIONS]: 1_000,
					},
				},
			],
		]),
		siegeAttributes: {
			maxHp: Number.MAX_SAFE_INTEGER,
			armor: 0,
			attack: {
				melee: 0,
				ranged: 0,
				siege: 0,
			},
			supplyLimit: 3,
			attackClicks: 0,
			grantBuffs: [],
		},
		action: TileAction.DISBAND,
	},
	[FrameName.SUPPLY_CARAVAN]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 46],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {},
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.GEMS]:
							premiumBuildingsPrices[FrameName.SUPPLY_CARAVAN],
					},
				},
			],
		]),
		siegeAttributes: {
			maxHp: Number.MAX_SAFE_INTEGER,
			armor: 0,
			attack: {
				melee: 0,
				ranged: 0,
				siege: 0,
			},
			supplyLimit: 5,
			attackClicks: 0,
			grantBuffs: [],
		},
		action: TileAction.DISBAND,
	},
	[FrameName.ARCHERS]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 60],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {},
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: 1_500_000,
						[ResourceType.LUMBER]: 150_000,
						[ResourceType.GOODS]: 50_000,
						[ResourceType.ARMY]: 750,
						[ResourceType.RATIONS]: 350,
					},
				},
			],
		]),
		siegeAttributes: {
			maxHp: 500,
			armor: 0,
			attack: {
				melee: 0,
				ranged: 2,
				siege: 0,
			},
			supplyLimit: -1,
			attackClicks: 2,
			grantBuffs: [
				{
					type: BuffType.ISOLATION_ATTACK,
					value: 1,
				},
			],
			buildingLimitFrameName: FrameName.ARCHERY_RANGE,
		},
		action: TileAction.DISBAND,
	},
	[FrameName.LIGHT_CAVALRY]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 69],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {},
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: 3_000_000,
						[ResourceType.IRON]: 100_000,
						[ResourceType.GOODS]: 25_000,
						[ResourceType.STEEL]: 100_000,
						[ResourceType.ARMY]: 1_500,
						[ResourceType.RATIONS]: 500,
					},
				},
			],
		]),
		siegeAttributes: {
			maxHp: 1_000,
			armor: 2,
			attack: {
				melee: 3,
				ranged: 0,
				siege: 0,
			},
			supplyLimit: -2,
			attackClicks: 3,
			grantBuffs: [
				{
					type: BuffType.ADD_ARMOR_SAME,
					value: 1,
				},
			],
			buildingLimitFrameName: FrameName.STABLES,
		},
		action: TileAction.DISBAND,
	},
	[FrameName.RANGERS]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 50],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {},
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.GEMS]:
							premiumBuildingsPrices[FrameName.RANGERS],
					},
				},
			],
		]),
		siegeAttributes: {
			maxHp: 750,
			armor: 2,
			attack: {
				melee: 2,
				ranged: 0,
				siege: 0,
			},
			supplyLimit: -1,
			attackClicks: 5,
			grantBuffs: [
				{
					type: BuffType.LOWER_ARMOR,
					value: 1,
				},
			],
			buildingLimitFrameName: FrameName.BARRACKS,
		},
		action: TileAction.DISBAND,
	},
	[FrameName.COMMAND_POST]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 54],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {},
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.GEMS]:
							premiumBuildingsPrices[FrameName.COMMAND_POST],
					},
				},
			],
		]),
		siegeAttributes: {
			maxHp: Number.MAX_SAFE_INTEGER,
			armor: 0,
			attack: {
				melee: 0,
				ranged: 0,
				siege: 0,
			},
			supplyLimit: -1,
			attackClicks: 15,
			grantBuffs: [
				{
					type: BuffType.ADD_ATTACK,
					value: 3,
				},
			],
		},
		action: TileAction.DISBAND,
	},
	[FrameName.SUPPLY_BOAT]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 72],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {},
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: 5_000_000,
						[ResourceType.LUMBER]: 750_000,
						[ResourceType.ARMY]: 1_500,
						[ResourceType.RATIONS]: 1_500,
					},
				},
			],
		]),
		siegeAttributes: {
			maxHp: Number.MAX_SAFE_INTEGER,
			armor: 0,
			attack: {
				melee: 0,
				ranged: 0,
				siege: 0,
			},
			supplyLimit: 4,
			attackClicks: 0,
			grantBuffs: [],
		},
		action: TileAction.DISBAND,
	},
	[FrameName.PATROL_BOAT]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 72],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {},
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: 2_500_000,
						[ResourceType.LUMBER]: 1_000_000,
						[ResourceType.IRON]: 50_000,
						[ResourceType.ARMY]: 1_000,
						[ResourceType.RATIONS]: 500,
					},
				},
			],
		]),
		siegeAttributes: {
			maxHp: 750,
			armor: 2,
			attack: {
				melee: 0,
				ranged: 2,
				siege: 0,
			},
			supplyLimit: -2,
			attackClicks: 1,
			grantBuffs: [
				{
					type: BuffType.LOWER_ARMOR,
					value: 1,
				},
			],
			buildingLimitFrameName: FrameName.MILITARY_SHIPYARD,
		},
		action: TileAction.DISBAND,
	},
	[FrameName.FIRE_GALLEY]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 75],
			[ResourceType.SETTLEMENT_CONNECTION, 0],
		]),
		productionBase: {},
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: 5_000_000,
						[ResourceType.LUMBER]: 1_500_000,
						[ResourceType.IRON]: 100_000,
						[ResourceType.GOODS]: 75_000,
						[ResourceType.STEEL]: 50_000,
						[ResourceType.ARMY]: 1_500,
						[ResourceType.RATIONS]: 1_000,
					},
				},
			],
		]),
		siegeAttributes: {
			maxHp: 1_000,
			armor: 1,
			attack: {
				melee: 0,
				ranged: 5,
				siege: 0,
			},
			supplyLimit: -5,
			attackClicks: 1,
			grantBuffs: [
				{
					type: BuffType.BATTLE_FRENZY_ATTACK,
					value: 1,
				},
			],
			buildingLimitFrameName: FrameName.MILITARY_SHIPYARD,
		},
		action: TileAction.DISBAND,
	},
};

config[FrameName.SETTLEMENT_CASTLE_LEVEL1] = Object.assign(
	{},
	config[FrameName.SETTLEMENT_CASTLE],
);
config[FrameName.SETTLEMENT_CASTLE_LEVEL2] = Object.assign(
	{},
	config[FrameName.SETTLEMENT_CASTLE],
);
config[FrameName.SETTLEMENT_CASTLE_LEVEL3] = Object.assign(
	{},
	config[FrameName.SETTLEMENT_CASTLE],
);
config[FrameName.SETTLEMENT_CASTLE_LEVEL4] = Object.assign(
	{},
	config[FrameName.SETTLEMENT_CASTLE],
);
config[FrameName.SETTLEMENT_CASTLE_LEVEL5] = Object.assign(
	{},
	config[FrameName.SETTLEMENT_CASTLE],
);
config[FrameName.SETTLEMENT_CASTLE_LEVEL6] = Object.assign(
	{},
	config[FrameName.SETTLEMENT_CASTLE],
);
config[FrameName.SETTLEMENT_CASTLE_LEVEL7] = Object.assign(
	{},
	config[FrameName.SETTLEMENT_CASTLE],
);
config[FrameName.SETTLEMENT_CASTLE_LEVEL8] = Object.assign(
	{},
	config[FrameName.SETTLEMENT_CASTLE],
);
config[FrameName.SETTLEMENT_CASTLE_LEVEL9] = Object.assign(
	{},
	config[FrameName.SETTLEMENT_CASTLE],
);
config[FrameName.SETTLEMENT_CASTLE_LEVEL10] = Object.assign(
	{},
	config[FrameName.SETTLEMENT_CASTLE],
);
config[FrameName.SHALLOW_SEA_ROCKS_LIGHTHOUSE] = Object.assign(
	{},
	config[FrameName.LIGHTHOUSE],
);
config[FrameName.MOUNTAINS_SCOUT_TOWER] = Object.assign(
	{},
	config[FrameName.HILLS_SCOUT_TOWER],
);
config[FrameName.CATTLE_RANGE_SPECIAL] = Object.assign(
	{},
	config[FrameName.CATTLE_RANGE],
);
config[FrameName.MOUNTAINS_BLAST_FURNACE] = Object.assign(
	{},
	config[FrameName.HILLS_BLAST_FURNACE],
);
config[FrameName.MOUNTAINS_LOOKOUT_TOWER] = Object.assign(
	{},
	config[FrameName.HILLS_LOOKOUT_TOWER],
);

export const getNewBuildingsFromLevel = (level: number): Array<FrameName> => {
	const newBuildings: Array<FrameName> = [];
	for (const [frameName, buildingConfig] of Object.entries(config)) {
		if (
			buildingConfig.requirements.has(ResourceType.LEVEL) &&
			buildingConfig.requirements.get(ResourceType.LEVEL)! === level
		) {
			newBuildings.push(frameName as FrameName);
		}
	}
	return newBuildings;
};

export const getBuildingConfig = (frameName: FrameName): TileConfigItem => {
	const buildingConfig = config?.[frameName];
	if (!buildingConfig)
		throw new Error(`Building config not found for type: ${frameName}!`);

	return buildingConfig;
};

export const isBuilding = (frameName: FrameName): boolean =>
	frameName in config;

export const getAllBuildingsForSearchTool = (): Array<FrameName> => {
	const buildings: Array<FrameName> = [];
	for (const [frameName, buildingConfig] of Object.entries(config)) {
		if (buildingConfig.inSearchTool) {
			buildings.push(frameName as FrameName);
		}
	}
	return buildings;
};
